import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Trash2, Eye, EyeOff, X, Cloud, Box, CheckCircle2, Circle, Sparkle, Sparkles, Plus, Zap, Image as ImageIcon, Wrench, Wand2, ScrollText } from 'lucide-react';
import * as C from '../constants';
import api from '../api';
import AlertToast from './AlertToast';
import ConfirmModal from './ConfirmModal';
import PresetModal from './PresetModal';
import SkillboxConfigModal from './SkillboxConfigModal';
import StoryboardMasterModal from './StoryboardMasterModal';
import FeatureSettingsModal from './FeatureSettingsModal';
import { sha256Hex } from '../utils/browserCompat'; // http 站点没有 crypto.subtle，去重要能降级

/**
 * ApiProviderModal — AI 模型 / API 配置独立弹窗
 *
 * 从原 ApiConfigModal 中拆分而来，只保留 AI Provider + 模型管理功能。
 * 左列：可添加 API 接口 + 工具箱
 * 中列：已添加模型列表
 * 右列：模型配置表单（API Key / 参数 / 积分等）
 */

// ── 共享样式 ──
const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 4000 };
const modalStyle = { width: 1300, height: '85vh', background: 'var(--modal-surface)', border: '1px solid var(--border-strong)', borderRadius: 12, color: 'var(--text-body)', boxShadow: '0 20px 60px rgba(0,0,0,0.6)', display: 'flex', flexDirection: 'column' };
const header = { padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.08)', fontWeight: 700, fontSize: 14, flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const bodyStyle = { display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 };
const leftPanel = { width: 280, borderRight: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', overflow: 'hidden', userSelect: 'none' };
const middlePanel = { width: 280, borderRight: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', overflow: 'hidden', userSelect: 'none' };
const rightPanel = { flex: 1, padding: 16, overflowY: 'auto' };
const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.12)', background: 'var(--modal-input-border)', color: 'var(--text-primary)', outline: 'none', fontSize: 13, boxSizing: 'border-box' };
const labelStyle = { fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' };
const btn = (primary) => ({ padding: '8px 16px', borderRadius: 8, border: '1px solid ' + (primary ? '#3b82f6' : 'rgba(255,255,255,0.12)'), background: primary ? '#3b82f6' : 'transparent', color: primary ? '#fff' : 'var(--text-body)', cursor: 'pointer', fontSize: 13 });

export default function ApiProviderModal({ open, onClose }) {
  const modalRef = useRef(null);
  const savedModelsListRef = useRef(null);
  const providersListRef = useRef(null);

  // ── 数据状态 ──
  const [loading, setLoading] = useState(true);
  const [providers, setProviders] = useState([]);
  const [removedProviders, setRemovedProviders] = useState([]);  // 🗑️ 已删除（隐藏）的接口 vendorId 名单
  const [savedModels, setSavedModels] = useState([]);  // 全部已保存模型（含工具箱模型）
  const [saving, setSaving] = useState(false);
  const [presetModalOpen, setPresetModalOpen] = useState(false);  // 🎨 预设工具箱弹窗
  const [skillboxModalOpen, setSkillboxModalOpen] = useState(false);  // ✨ 技能工具箱弹窗
  const [storyboardModalOpen, setStoryboardModalOpen] = useState(false);  // 🎬 分镜大师设定弹窗
  const [modelMasterModalOpen, setModelMasterModalOpen] = useState(false);  // 📝 建模大师设定弹窗
  const [textPolishModalOpen, setTextPolishModalOpen] = useState(false);  // ✨ 文本润色设定弹窗

  // ── 拖拽排序 ──
  const [dragModelId, setDragModelId] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const [dragProviderId, setDragProviderId] = useState(null);
  const [dropProviderTargetId, setDropProviderTargetId] = useState(null);
  const modelDragStartRef = useRef(null);
  const providerDragStartRef = useRef(null);

  // ── 选中状态 ──
  // null | { type: 'provider', vendorId } | { type: 'saved', id }
  const [activeSelection, setActiveSelection] = useState(null);

  // ── 模型表单字段 ──
  const [selectedModel, setSelectedModel] = useState('');
  const [modelType, setModelType] = useState('image');
  const [apiKey, setApiKey] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [enabledParams, setEnabledParams] = useState({});
  const [showKey, setShowKey] = useState(false);
  const [volcAccessKeyId, setVolcAccessKeyId] = useState('');
  const [volcSecretAccessKey, setVolcSecretAccessKey] = useState('');
  const [showVolcKeyId, setShowVolcKeyId] = useState(false);
  const [showVolcKeySecret, setShowVolcKeySecret] = useState(false);
  const [creditCost, setCreditCost] = useState(0);
  const [multipliers, setMultipliers] = useState({});
  const [logoUrl, setLogoUrl] = useState('');
  const [description, setDescription] = useState('');
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoLoadError, setLogoLoadError] = useState(false);
  // ── LoRA 配置 ──
  const [loraList, setLoraList] = useState([]);  // [{ name, label, thumbnail }]
  // ── Liblib ComfyUI 工作流模板配置 ──
  const [liblibTemplateUuid, setLiblibTemplateUuid] = useState('4df2efa0f18d46dc9758803e478eb51c');
  const [liblibWorkflowUuid, setLiblibWorkflowUuid] = useState('e8dfcc632ce141829a32c806ebff2055');
  const [liblibImageNodeId, setLiblibImageNodeId] = useState('62');
  const [liblibImageInputKey, setLiblibImageInputKey] = useState('image');
  const [liblibPromptNodeId, setLiblibPromptNodeId] = useState('');
  const [liblibPromptInputKey, setLiblibPromptInputKey] = useState('prompt');
  const [liblibGenerateTemplate, setLiblibGenerateTemplate] = useState('');

  // ── Midjourney 多能力模型专用状态（每组对应一个 model id） ──
  const [mjRealNames, setMjRealNames] = useState({});         // { [modelId]: string }
  const [mjDisplayNames, setMjDisplayNames] = useState({});    // { [modelId]: string }
  const [mjDescriptions, setMjDescriptions] = useState({});    // { [modelId]: string }
  const [mjCreditCosts, setMjCreditCosts] = useState({});      // { [modelId]: number }
  const [mjEnabledParams, setMjEnabledParams] = useState({});  // { [modelId]: { [paramKey]: string[] } }
  const [mjMultipliers, setMjMultipliers] = useState({});      // { [modelId]: { [paramKey]: { [option]: number } } }

  // Helper: update MJ per-model fields
  const updateMjRealName = (modelId, value) => setMjRealNames(prev => ({ ...prev, [modelId]: value }));
  const updateMjDisplayName = (modelId, value) => setMjDisplayNames(prev => ({ ...prev, [modelId]: value }));
  const updateMjDescription = (modelId, value) => setMjDescriptions(prev => ({ ...prev, [modelId]: value }));
  const updateMjCreditCost = (modelId, value) => setMjCreditCosts(prev => ({ ...prev, [modelId]: value }));
  const normalizeVersionValue = (value) => {
    if (typeof value !== 'string') return value;
    const cleaned = value.replace(/[\u200B-\u200D\uFEFF]/g, '').trim();
    const compact = cleaned.replace(/\s+/g, '').toLowerCase();

    const nijiMatch = compact.match(/^--niji(\d+(?:\.\d+)?)$/);
    if (nijiMatch) return `-- niji ${nijiMatch[1]}`;

    const vMatch = compact.match(/^--v(\d+(?:\.\d+)?)$/);
    if (vMatch) return `--v ${vMatch[1]}`;

    return cleaned;
  };
  const normalizeParamValue = (paramKey, value) => {
    if (paramKey === 'version') return normalizeVersionValue(value);
    return value;
  };
  const orderSelectedValuesByOptions = (paramKey, selectedValues, options) => {
    const normalizedSelected = new Set((Array.isArray(selectedValues) ? selectedValues : [])
      .map((v) => normalizeParamValue(paramKey, v)));
    return (Array.isArray(options) ? options : [])
      .map((opt) => normalizeParamValue(paramKey, opt))
      .filter((opt) => normalizedSelected.has(opt));
  };
  const normalizeEnabledParamsForModel = (rawEnabledParams, modelDef, rawMultipliers = {}) => {
    const source = rawEnabledParams || {};
    const result = {};

    // 🛡️ 未匹配到模型静态定义（如「真实模型名」被改成自定义值、provider 未注册等）时，
    //    绝不清空已有参数配置——原样保留 source，避免把分辨率/比例等配置误删。
    const hasDef = modelDef && modelDef.parameters && Object.keys(modelDef.parameters).length > 0;
    if (!hasDef) {
      const { _multipliers: srcMults, ...restSource } = source;
      const mults = (rawMultipliers && Object.keys(rawMultipliers).length > 0) ? rawMultipliers : (srcMults || {});
      return { enabledParameters: restSource, multipliers: mults };
    }

    const isToolboxDef = modelDef?.category === 'toolbox';
    Object.entries(modelDef?.parameters || {}).forEach(([key, param]) => {
      if (param.type === 'select' && Array.isArray(param.options)) {
        // 工具箱/语言模型：select 是「单值下拉」（如 支持视觉 是/否），原样存字符串；
        // 图像/视频模型：select 是「可启用选项集合」，存数组子集。
        if (isToolboxDef) {
          const v = source[key];
          result[key] = (typeof v === 'string' && v) ? v : (param.default ?? param.options[0] ?? '');
        } else {
          const ordered = orderSelectedValuesByOptions(key, source[key], param.options);
          result[key] = ordered;
        }
      } else if (param.type === 'boolean') {
        const current = Array.isArray(source[key]) ? source[key] : [true, false];
        result[key] = [true, false].filter((v) => current.includes(v));
      } else if (param.type === 'number' && Array.isArray(param.options)) {
        // type=number + options 存在时（如 duration），保存可选值数组而不是单个值
        const ordered = orderSelectedValuesByOptions(key, source[key], param.options);
        result[key] = ordered;
      } else if (param.type === 'text' || param.type === 'textarea' || param.type === 'number') {
        result[key] = source[key] ?? param.default ?? '';
      } else if (param.type === 'style_presets') {
        // 🎨 画面风格预设：原样保留数组（每项 {id,name,logoUrl,prompt}）
        result[key] = Array.isArray(source[key]) ? source[key] : [];
      } else if (param.type === 'lora') {
        // 🎨 LoRA 配置：保留 loras 数组（每项 {name,label,thumbnail}）
        result[key] = source[key] || { loras: [] };
      }
    });

    if (source._dependsOn) result._dependsOn = source._dependsOn;
    if (source._actionLabels) result._actionLabels = source._actionLabels;
    if (source._disabledActions) result._disabledActions = source._disabledActions;

    const sourceMults = rawMultipliers && Object.keys(rawMultipliers).length > 0
      ? rawMultipliers
      : (source._multipliers || {});
    const normalizedMults = {};

    Object.entries(sourceMults || {}).forEach(([paramKey, paramMults]) => {
      if (!paramMults || typeof paramMults !== 'object') return;
      const selected = Array.isArray(result[paramKey]) ? result[paramKey] : [];
      if (selected.length === 0) return;
      const row = {};
      selected.forEach((opt) => {
        const direct = paramMults[opt];
        const normalizedKey = normalizeParamValue(paramKey, opt);
        const legacyNiji = (paramKey === 'version' && typeof normalizedKey === 'string')
          ? normalizedKey.replace(/^--\s*niji\s+/i, '--niji ')
          : normalizedKey;
        const fallback = paramMults[legacyNiji];
        const val = Number(direct ?? fallback);
        if (Number.isFinite(val) && val > 0) {
          row[normalizedKey] = val;
        }
      });
      if (Object.keys(row).length > 0) {
        normalizedMults[paramKey] = row;
      }
    });

    return { enabledParameters: result, multipliers: normalizedMults };
  };
  const updateMjParam = (modelId, paramKey, option) => {
    setMjEnabledParams(prev => {
      const modelParams = prev[modelId] || {};
      const currentOptions = modelParams[paramKey] || [];
      const normalizedOption = normalizeParamValue(paramKey, option);
      const normalizedCurrent = currentOptions.map((v) => normalizeParamValue(paramKey, v));
      const toggled = normalizedCurrent.includes(normalizedOption)
        ? normalizedCurrent.filter(o => o !== normalizedOption)
        : [...normalizedCurrent, normalizedOption];
      const mjVendor = providers.find((p) => p.vendorId === 'yunwu-midjourney');
      const modelDef = mjVendor?.models?.find((m) => m.id === modelId);
      const ordered = modelDef?.parameters?.[paramKey]?.options
        ? orderSelectedValuesByOptions(paramKey, toggled, modelDef.parameters[paramKey].options)
        : [...new Set(toggled)];
      return { ...prev, [modelId]: { ...modelParams, [paramKey]: ordered } };
    });
  };
  const updateMjMultiplier = (modelId, paramKey, option, value) => {
    setMjMultipliers(prev => ({
      ...prev,
      [modelId]: {
        ...(prev[modelId] || {}),
        [paramKey]: {
          ...(prev[modelId]?.[paramKey] || {}),
          [option]: value
        }
      }
    }));
  };

  // ── 多账号实例 ──
  const [modelInstances, setModelInstances] = useState([]);
  const [selectedInstanceIdx, setSelectedInstanceIdx] = useState(0);
  const [instancesLoading, setInstancesLoading] = useState(false);
  const [showInstanceKey, setShowInstanceKey] = useState(false);

  // ── Toast / 确认弹窗 ──
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const [confirmModal, setConfirmModal] = useState(null);

  // 🛡️ Toast 定时器清理：防止组件卸载后 setState 泄漏
  const toastTimerRef = useRef(null);
  const showToast = (msg, type = 'success', duration = 5000) => {
    clearTimeout(toastTimerRef.current);
    setToastMessage(msg);
    setToastType(type);
    toastTimerRef.current = setTimeout(() => setToastMessage(''), duration);
  };
  useEffect(() => () => clearTimeout(toastTimerRef.current), []);

  // 🎨 通用 Logo 上传：返回上传后的 URL（失败返回 ''）。供画面风格预设等复用。
  const uploadLogoFile = async (file) => {
    if (!file) return '';
    try {
      const buffer = await file.arrayBuffer();
      // 拿不到哈希（http 非安全上下文）就跳过去重预检，直接上传
      const hashHex = await sha256Hex(buffer);
      if (hashHex) {
        const checkResult = await api.get(`/api/upload/check-hash?hash=${hashHex}&type=logo`);
        if (checkResult.exists && checkResult.url) return checkResult.url;
      }
      const formData = new FormData(); formData.append('file', file);
      const { getToken: getAuthToken } = await import('../api');
      const result = await fetch(`${C.API_BASE_URL}/api/upload?type=logo`, { method: 'POST', headers: { 'Authorization': `Bearer ${getAuthToken()}` }, body: formData }).then(r => r.json());
      if (result.url) return result.url;
      if (result.error) showToast('❌ 上传失败: ' + result.error, 'warning');
    } catch (err) {
      console.error('上传 Logo 失败:', err);
      showToast('❌ 上传失败', 'warning');
    }
    return '';
  };

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  //  数据加载
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      api.get('/api/providers'),
      api.get('/api/admin/models'),
    ]).then(([providerData, modelsData]) => {
      // Provider 排序
      const pData = providerData.providers || [];
      const savedOrder = localStorage.getItem('providerOrder');
      let orderedProviders = pData;
      if (savedOrder) {
        try {
          const orderArray = JSON.parse(savedOrder);
          orderedProviders = [
            ...orderArray.map(id => pData.find(p => p.vendorId === id)).filter(Boolean),
            ...pData.filter(p => !orderArray.includes(p.vendorId))
          ];
        } catch (e) { /* ignore */ }
      }
      setProviders(orderedProviders);
      setRemovedProviders(Array.isArray(providerData.removedProviders) ? providerData.removedProviders : []);

      setSavedModels(modelsData || []);
    }).then(() => {
      setActiveSelection(null);
      resetForm();
    }).catch(err => {
      console.error('加载 API 配置失败', err);
    }).finally(() => {
      setLoading(false);
    });
  }, [open]);

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  //  表单重置
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const resetForm = () => {
    setSelectedModel(''); setModelType('image'); setApiKey(''); setBaseUrl('');
    setDisplayName(''); setEnabledParams({}); setShowKey(false);
    setCreditCost(0); setMultipliers({}); setLogoUrl(''); setLogoLoadError(false);
    setDescription('');
    setVolcAccessKeyId(''); setVolcSecretAccessKey('');
    setShowVolcKeyId(false); setShowVolcKeySecret(false);
    setModelInstances([]); setSelectedInstanceIdx(0); setShowInstanceKey(false);
    setLiblibTemplateUuid('4df2efa0f18d46dc9758803e478eb51c');
    setLiblibWorkflowUuid('e8dfcc632ce141829a32c806ebff2055');
    setLiblibImageNodeId('62');
    setLiblibImageInputKey('image');
    setLiblibPromptNodeId('');
    setLiblibPromptInputKey('prompt');
    setLiblibGenerateTemplate('');
  };

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  //  拖拽排序 — 已添加模型
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const handleModelPointerDown = (e, modelId) => {
    if (e.target.closest('[data-model-btn]')) return;
    e.stopPropagation();
    modelDragStartRef.current = { id: modelId, el: e.currentTarget, pointerId: e.pointerId };
  };
  const handleModelPointerMove = (e) => {
    if (!dragModelId && modelDragStartRef.current) {
      const start = modelDragStartRef.current;
      setDragModelId(start.id);
      try { start.el.setPointerCapture(start.pointerId); } catch (_) {}
      modelDragStartRef.current = null;
    }
    if (!dragModelId) return;
    e.stopPropagation();
    const listEl = savedModelsListRef.current;
    if (!listEl) return;
    const items = listEl.querySelectorAll('[data-model-id]');
    const y = e.clientY;
    let closestId = null;
    items.forEach(item => {
      const rect = item.getBoundingClientRect();
      if (y >= rect.top && y <= rect.bottom) closestId = item.getAttribute('data-model-id');
    });
    setDropTargetId(closestId);
  };
  const handleModelPointerUp = () => {
    modelDragStartRef.current = null;
    if (!dragModelId) return;
    if (dropTargetId && dropTargetId !== dragModelId) {
      const fromIndex = savedModels.findIndex(m => String(m.id) === String(dragModelId));
      const toIndex = savedModels.findIndex(m => String(m.id) === String(dropTargetId));
      if (fromIndex !== -1 && toIndex !== -1) {
        const newModels = [...savedModels];
        const [movedItem] = newModels.splice(fromIndex, 1);
        newModels.splice(toIndex, 0, movedItem);
        setSavedModels(newModels);
        // 仅把“普通模型”顺序写入全局排序；工具箱模型不参与节点下拉排序。
        const reorderedNormalIds = newModels.filter(m => !isToolboxModel(m)).map(m => Number(m.id));
        // 保存后所有客户端都将按同一顺序读取（不依赖本地浏览器缓存）。
        api.post('/api/admin/models/order', { modelIds: reorderedNormalIds })
          .then(() => {
            window.dispatchEvent(new Event('modelsUpdated'));
            showToast('✅ 模型顺序已全局保存', 'success', 2000);
          })
          .catch((e) => {
            console.error('保存模型顺序失败', e);
            showToast('❌ 模型顺序保存失败：' + (e?.message || '未知错误'), 'warning');
          });
      }
    }
    setDragModelId(null); setDropTargetId(null);
  };

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  //  拖拽排序 — 可添加接口
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const handleProviderPointerDown = (e, vendorId) => {
    if (e.target.closest('[data-provider-btn]')) return;
    e.stopPropagation();
    providerDragStartRef.current = { id: vendorId, el: e.currentTarget, pointerId: e.pointerId };
  };
  const handleProviderPointerMove = (e) => {
    if (!dragProviderId && providerDragStartRef.current) {
      const start = providerDragStartRef.current;
      setDragProviderId(start.id);
      try { start.el.setPointerCapture(start.pointerId); } catch (_) {}
      providerDragStartRef.current = null;
    }
    if (!dragProviderId) return;
    e.stopPropagation();
    const listEl = providersListRef.current;
    if (!listEl) return;
    const items = listEl.querySelectorAll('[data-provider-id]');
    const y = e.clientY;
    let closestId = null;
    items.forEach(item => {
      const rect = item.getBoundingClientRect();
      if (y >= rect.top && y <= rect.bottom) closestId = item.getAttribute('data-provider-id');
    });
    setDropProviderTargetId(closestId);
  };
  const handleProviderPointerUp = () => {
    providerDragStartRef.current = null;
    if (!dragProviderId) return;
    if (dropProviderTargetId && dropProviderTargetId !== dragProviderId) {
      const fromIndex = providers.findIndex(p => p.vendorId === dragProviderId);
      const toIndex = providers.findIndex(p => p.vendorId === dropProviderTargetId);
      if (fromIndex !== -1 && toIndex !== -1) {
        const newProviders = [...providers];
        const [movedItem] = newProviders.splice(fromIndex, 1);
        newProviders.splice(toIndex, 0, movedItem);
        setProviders(newProviders);
        localStorage.setItem('providerOrder', JSON.stringify(newProviders.map(p => p.vendorId)));
      }
    }
    setDragProviderId(null); setDropProviderTargetId(null);
  };

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  //  选中 Provider（新建模型）
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const handleSelectProvider = (vendorId) => {
    const vendor = providers.find(p => p.vendorId === vendorId);
    if (!vendor) return;
    setActiveSelection({ type: 'provider', vendorId });
    setBaseUrl(vendor.baseUrl || '');
    if (vendor.models?.length > 0) {
      const firstModel = vendor.models[0];
      setSelectedModel(firstModel.id);
      setDisplayName(firstModel.name);
      setModelType(firstModel.type === 'video' ? 'video' : 'image');
      // 接口地址：优先用首个模型自带的 apiEndpoint（如豆包 Ark）；标记 customEndpoint 的口清空自填；否则退回 vendor.baseUrl
      if (firstModel.apiEndpoint) setBaseUrl(firstModel.apiEndpoint);
      else if (firstModel.customEndpoint) setBaseUrl('');
      const isToolboxDef0 = firstModel.category === 'toolbox';
      const allEnabled = {};
      Object.entries(firstModel.parameters || {}).forEach(([key, param]) => {
        if (param.type === 'select' && param.options) allEnabled[key] = isToolboxDef0 ? (param.default ?? param.options[0]) : [...param.options];
        else if (param.type === 'boolean') allEnabled[key] = [true, false];
        else if (param.type === 'textarea' || param.type === 'text') allEnabled[key] = param.default || '';
      });
      // 提取参数联动配置（dependsOn）→ 存入 enabled_parameters._dependsOn
      // 供前端 NodeConsole 做 cascade 过滤（父参数变化时动态筛选子参数可选项）
      const dependsOn = {};
      Object.entries(firstModel.parameters || {}).forEach(([key, param]) => {
        if (param.dependsOn) dependsOn[key] = param.dependsOn;
      });
      if (Object.keys(dependsOn).length > 0) allEnabled._dependsOn = dependsOn;
      setEnabledParams(allEnabled);
      const defaultMults = {};
      Object.entries(firstModel.parameters || {}).forEach(([key, param]) => {
        if (param.affectsPrice && param.defaultMultipliers) defaultMults[key] = { ...param.defaultMultipliers };
      });
      setMultipliers(defaultMults);
    }
    setApiKey(''); setShowKey(false);
    setVolcAccessKeyId(''); setVolcSecretAccessKey('');
    setShowVolcKeyId(false); setShowVolcKeySecret(false);
    // 🛡️ 新建模型必须清空多账号(口)，否则会把上一个模型残留的账号(含API Key)串到新模型
    setModelInstances([]); setSelectedInstanceIdx(0); setShowInstanceKey(false);

    if (vendorId === 'liblib-comfyui') {
      setLiblibTemplateUuid('4df2efa0f18d46dc9758803e478eb51c');
      setLiblibWorkflowUuid('e8dfcc632ce141829a32c806ebff2055');
      setLiblibImageNodeId('62');
      setLiblibImageInputKey('image');
      setLiblibPromptNodeId('');
      setLiblibPromptInputKey('prompt');
      setLiblibGenerateTemplate('');
    }
  };

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  //  选中已保存模型
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const handleSelectSaved = (model) => {
    setActiveSelection({ type: 'saved', id: model.id });
    const apiKeyValue = model.api_key || '';
    setApiKey(apiKeyValue);
    const vendor = providers.find(p => p.vendorId === model.type);
    if (vendor?.vendorId === 'jimeng-volcengine' && apiKeyValue.includes(',')) {
      const [accessKeyId, secretAccessKey] = apiKeyValue.split(',');
      setVolcAccessKeyId(accessKeyId?.trim() || '');
      setVolcSecretAccessKey(secretAccessKey?.trim() || '');
    } else {
      setVolcAccessKeyId(''); setVolcSecretAccessKey('');
    }
    setDisplayName(model.name || '');
    setModelType(model.model_type || 'image');
    setCreditCost(model.credit_cost || 0);
    setLogoUrl(model.logo_url || '');
    setLogoLoadError(false);
    setDescription(model.description || '');
    let modelId = '';
    try {
      const extra = typeof model.extra_json === 'string' ? JSON.parse(model.extra_json) : model.extra_json;
      modelId = extra?.model_id || '';
      if (vendor?.vendorId === 'liblib-comfyui') {
        setLiblibTemplateUuid(extra?.template_uuid || '4df2efa0f18d46dc9758803e478eb51c');
        setLiblibWorkflowUuid(extra?.workflow_uuid || 'e8dfcc632ce141829a32c806ebff2055');
        setLiblibImageNodeId(extra?.image_node_id || '62');
        setLiblibImageInputKey(extra?.image_input_key || 'image');
        setLiblibPromptNodeId(extra?.prompt_node_id || '');
        setLiblibPromptInputKey(extra?.prompt_input_key || 'prompt');
        setLiblibGenerateTemplate(extra?.generate_params_template || '');
      }
    } catch (e) {}
    setSelectedModel(modelId);
    let apiUrl = model.api_url || '';
    const modelDef = vendor?.models?.find(m => m.id === modelId);
    // 优先用已保存的 api_url —— 尊重管理员自填地址（如「通用 OpenAI 兼容」口）。
    // 仅当从未保存过地址时，才回退到模型自带 apiEndpoint 或 vendor.baseUrl。
    // （旧逻辑无条件用 apiEndpoint/baseUrl 覆盖，导致通用口每次编辑都被重置成 DeepSeek）
    if (!apiUrl) apiUrl = modelDef?.apiEndpoint || vendor?.baseUrl || '';
    setBaseUrl(apiUrl);
    let params = {};
    try { params = typeof model.enabled_parameters === 'string' ? JSON.parse(model.enabled_parameters) : (model.enabled_parameters || {}); } catch (e) {}
    setEnabledParams(params);
    setMultipliers(params._multipliers || {});
    // 加载 LoRA 列表配置
    if (params.lora && Array.isArray(params.lora.loras)) {
      setLoraList(params.lora.loras);
    } else {
      setLoraList([]);
    }
    setShowKey(false);
    setSelectedInstanceIdx(0);
    setShowInstanceKey(false);
    setModelInstances([]);  // 🛡️ 先同步清空，避免 GET 返回前残留上一个模型的账号（saved→saved 竞态）
    (async () => {
      try {
        setInstancesLoading(true);
        const instData = await api.get(`/api/admin/models/${model.id}/instances`);
        const loaded = instData.instances || [];
        if (loaded.length > 0) setModelInstances(loaded);
        else if (apiKeyValue) setModelInstances([{ label: '默认账号', api_key: apiKeyValue, max_concurrency: 5 }]);
        else setModelInstances([]);
      } catch (e) {
        console.warn('加载实例失败', e);
        setModelInstances([]);
      } finally { setInstancesLoading(false); }
    })();
  };

  // ── 模型下拉变化 ──
  const handleModelChange = (modelId) => {
    setSelectedModel(modelId);
    const vendor = activeSelection?.type === 'provider' ? providers.find(p => p.vendorId === activeSelection.vendorId) : null;
    const model = vendor?.models?.find(m => m.id === modelId);
    if (model) {
      setDisplayName(model.name || modelId);
      setModelType(model.type === 'video' ? 'video' : 'image');
      // 接口地址：模型自带 apiEndpoint 优先；标记 customEndpoint 的口（如「通用 OpenAI 兼容」）
      // 清空让管理员自填，避免误继承 vendor.baseUrl（DeepSeek）；其余退回 vendor.baseUrl。
      if (model.apiEndpoint) setBaseUrl(model.apiEndpoint);
      else if (model.customEndpoint) setBaseUrl('');
      else if (vendor?.baseUrl) setBaseUrl(vendor.baseUrl);
      const isToolboxDef = model.category === 'toolbox';
      const allEnabled = {};
      Object.entries(model.parameters || {}).forEach(([key, param]) => {
        // 工具箱模型的 select 是单值下拉（如「支持视觉 是/否」），按默认值存单个字符串；
        // 普通图像/视频模型的 select 是「可选项闸门」，存全部可选项数组。
        if (param.type === 'select' && param.options) allEnabled[key] = isToolboxDef ? (param.default ?? param.options[0]) : [...param.options];
        else if (param.type === 'boolean') allEnabled[key] = [true, false];
        else if (param.type === 'camera_movement') allEnabled[key] = [];
        else if (param.type === 'text' || param.type === 'textarea') allEnabled[key] = param.default || '';
      });
      // LoRA 参数：从模型定义中读取初始 LoRA 列表
      const loraParam = model.parameters?.lora;
      if (loraParam && loraParam.type === 'lora' && loraParam.loras) {
        setLoraList([...loraParam.loras]);
      } else {
        setLoraList([]);
      }
      // 提取参数联动配置（dependsOn）→ 存入 enabled_parameters._dependsOn
      const dependsOn = {};
      Object.entries(model.parameters || {}).forEach(([key, param]) => {
        if (param.dependsOn) dependsOn[key] = param.dependsOn;
      });
      if (Object.keys(dependsOn).length > 0) allEnabled._dependsOn = dependsOn;
      setEnabledParams(allEnabled);
      const defaultMults = {};
      Object.entries(model.parameters || {}).forEach(([key, param]) => {
        if (param.affectsPrice && param.defaultMultipliers) defaultMults[key] = { ...param.defaultMultipliers };
      });
      setMultipliers(defaultMults);
    }
  };

  // ── 参数勾选 ──
  const handleParamToggle = (paramKey, value) => {
    setEnabledParams(prev => {
      const current = prev[paramKey] || [];
      const normalizedValue = normalizeParamValue(paramKey, value);
      const normalizedCurrent = current.map((v) => normalizeParamValue(paramKey, v));
      const toggled = normalizedCurrent.includes(normalizedValue)
        ? normalizedCurrent.filter(v => v !== normalizedValue)
        : [...normalizedCurrent, normalizedValue];
      const modelOptions = currentModel?.parameters?.[paramKey]?.options;
      const newValues = modelOptions
        ? orderSelectedValuesByOptions(paramKey, toggled, modelOptions)
        : [...new Set(toggled)];
      return { ...prev, [paramKey]: newValues };
    });
  };

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  //  保存（新建 / 更新）
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const handleSave = async () => {
    const vendorId = activeSelection?.type === 'provider'
      ? activeSelection.vendorId
      : savedModels.find(m => m.id === activeSelection.id)?.type;

    // 验证
    if (activeSelection?.type === 'saved') {
      if (!selectedModel || modelInstances.length === 0 || !modelType) {
        showToast('⚠️ 请填写完整：选择模型、添加至少一个账号、选择模型类别', 'warning'); return;
      }
    } else if (vendorId === 'jimeng-volcengine') {
      if (!activeSelection || !selectedModel || !volcAccessKeyId || !volcSecretAccessKey || !modelType) {
        showToast('⚠️ 请填写完整：选择节点、模型、Access Key ID、Secret Access Key、模型类别', 'warning'); return;
      }
    } else if (vendorId === 'yunwu-midjourney') {
      if (!activeSelection || !selectedModel || !apiKey) {
        showToast('⚠️ 请填写完整：选择能力模型并填写 API Key', 'warning'); return;
      }
    } else {
      if (!activeSelection || !selectedModel || !apiKey || !modelType) {
        showToast('⚠️ 请填写完整：选择节点、模型、API Key、模型类别', 'warning'); return;
      }
    }
    if (!vendorId) {
      showToast('⚠️ 无法确定厂商ID', 'warning'); return;
    }

    let finalApiKey;
    if (activeSelection?.type === 'saved' && modelInstances.length > 0) finalApiKey = modelInstances[0].api_key || '';
    else if (vendorId === 'jimeng-volcengine') finalApiKey = `${volcAccessKeyId},${volcSecretAccessKey}`;
    else finalApiKey = apiKey;

    setSaving(true);
    try {
      /**
       * ── 工具箱模型 (category: 'toolbox') 保存逻辑说明 ──
       *
       * 工具箱模型（如 GPT 局部重绘 gpt_inpaint）与普通模型的区别：
       *
       * 1. extra_json 中带 { is_toolbox: true } 标记
       *    → 后端 /api/toolbox-config 据此查询所有工具箱模型
       *    → 前端 EditorPage.js 据此决定是否显示对应工具条胶囊
       *
       * 2. 不加入 model_mappings 表
       *    → 不会出现在前端模型选择器下拉列表中
       *    → 用户无法在生成面板里选到工具箱模型
       *
       * 3. 自动启用所有参数（不需要管理员逐个勾选）
       *    → 见下方 model.category === 'toolbox' 分支
       *
       * 工具箱模型可存在于两种 vendor 分支：
       *   - MJ 分支（yunwu-midjourney 多能力）：如 mj_inpaint
       *   - 普通分支：如 gpt_inpaint（yunwu-gptimage2）
       * 两个分支都需要处理 is_toolbox 标记和跳过 model_mappings。
       */

      // Midjourney 单能力模型处理（按当前选中模型配置）
      if (vendorId === 'yunwu-midjourney' && currentVendor?.models) {
        const model = currentVendor.models.find(m => m.id === selectedModel);
        if (!model) {
          showToast('❌ 未找到当前选择的 Midjourney 能力模型', 'warning');
          setSaving(false);
          return;
        }

        const isEditingSavedMj = activeSelection?.type === 'saved';

        const modelEnabledParams = isEditingSavedMj ? (enabledParams || {}) : (mjEnabledParams[model.id] || {});
        const modelMultipliers = isEditingSavedMj ? (multipliers || {}) : (mjMultipliers[model.id] || {});
        const modelCreditCost = isEditingSavedMj ? (creditCost ?? 0) : (mjCreditCosts[model.id] ?? model.creditCost ?? 0);
        const modelDisplayName = isEditingSavedMj ? (displayName || '') : (mjDisplayNames[model.id] || '');
        const modelDescription = isEditingSavedMj ? (description || '') : (mjDescriptions[model.id] || '');
        const modelRealName = isEditingSavedMj ? (selectedModel || model.id) : (mjRealNames[model.id] || model.id);

        // 工具箱模型：自动启用所有参数（不需要用户复选勾选）
        let finalEnabledParams = modelEnabledParams;
        if (model.category === 'toolbox' && model.parameters) {
          finalEnabledParams = {};
          Object.entries(model.parameters).forEach(([key, param]) => {
            if (param.type === 'select' && param.options) finalEnabledParams[key] = [...param.options];
            else if (param.type === 'boolean') finalEnabledParams[key] = [true, false];
            else if (param.type === 'textarea' || param.type === 'text') {
              finalEnabledParams[key] = modelEnabledParams[key] || param.default || '';
            }
            // 🎨 画面风格预设：保留数组（每项 {id,name,logoUrl,prompt}）
            else if (param.type === 'style_presets') {
              finalEnabledParams[key] = Array.isArray(modelEnabledParams[key]) ? modelEnabledParams[key] : [];
            }
          });
          // 保留自定义动作名称
          if (modelEnabledParams._actionLabels) {
            finalEnabledParams._actionLabels = modelEnabledParams._actionLabels;
          }
          // 保留禁用的动作列表
          if (modelEnabledParams._disabledActions?.length) {
            finalEnabledParams._disabledActions = modelEnabledParams._disabledActions;
          }
        }

        // 提取参数联动配置（dependsOn）→ 存入 enabled_parameters._dependsOn
        if (model.parameters) {
          const dependsOn = {};
          Object.entries(model.parameters).forEach(([key, param]) => {
            if (param.dependsOn) dependsOn[key] = param.dependsOn;
          });
          if (Object.keys(dependsOn).length > 0) finalEnabledParams._dependsOn = dependsOn;
        }

        const normalizedMj = normalizeEnabledParamsForModel(finalEnabledParams, model, modelMultipliers);

        const mjPayload = {
          name: modelDisplayName || model.name,
          type: vendorId,
          model_type: model.type === 'both' ? 'image' : model.type,
          api_url: baseUrl,
          api_key: finalApiKey,
          extra_json: JSON.stringify({
            model_id: modelRealName,
            // 工具箱模型标记：后端 /api/toolbox-config 据此查询，前端据此显示工具条胶囊
            ...(model.category === 'toolbox' ? { is_toolbox: true } : {})
          }),
          enabled_parameters: {
            ...normalizedMj.enabledParameters,
            _multipliers: Object.keys(normalizedMj.multipliers).length > 0 ? normalizedMj.multipliers : undefined
          },
          credit_cost: modelCreditCost,
          logo_url: logoUrl || null,
          description: modelDescription || null,
        };

        if (isEditingSavedMj) {
          await api.put(`/api/admin/models/${activeSelection.id}`, mjPayload);
          // 🔧 MJ 模型也需要保存 instances（并发设置、多账号等）
          // 注意：MJ 保存路径与普通模型分开（上方的 if/else），需单独调用 instances 接口
          const instancesToSave = (activeSelection.type === 'saved' && modelInstances.length > 0)
            ? modelInstances
            : (finalApiKey ? [{ label: '默认账号', api_key: finalApiKey, max_concurrency: 5 }] : []);
          if (instancesToSave.length > 0) {
            try { await api.post(`/api/admin/models/${activeSelection.id}/instances`, { instances: instancesToSave }); } catch (e) { console.warn('保存实例失败', e); }
          }
        } else {
          // 查找是否已存在该模型（兼容 extra_json 字符串和对象）
          const existingModel = (savedModels || []).find(m => {
            if (m.type !== vendorId) return false;
            let mid = '';
            try {
              const extra = typeof m.extra_json === 'string' ? JSON.parse(m.extra_json) : (m.extra_json || {});
              mid = extra.model_id || '';
            } catch (e) {
              mid = '';
            }
            return mid === modelRealName;
          });

          if (existingModel) {
            await api.put(`/api/admin/models/${existingModel.id}`, mjPayload);
          } else {
            const data = await api.post('/api/admin/add-model', mjPayload);
            const savedId = data.id;
            // 工具箱模型不加入 model_mappings → 不出现在模型选择器下拉列表中
            // 仅通过 /api/toolbox-config 接口暴露给前端工具条使用
            if (model.category !== 'toolbox') {
              const currentMappings = await api.get('/api/admin/model-mappings');
              const currentIds = (currentMappings[mjPayload.model_type] || []).map(m => m.id);
              if (!currentIds.includes(savedId)) {
                await api.post('/api/admin/model-mappings', { media_type: mjPayload.model_type, modelIds: [...currentIds, savedId] });
              }
            }
          }
        }

        // 刷新列表
        const modelsData = await api.get('/api/admin/models');
        setSavedModels(modelsData || []);
        window.dispatchEvent(new Event('modelsUpdated'));

        showToast(isEditingSavedMj ? '✅ Midjourney 配置更新成功！' : '✅ Midjourney 当前能力配置已保存！', 'success', 3000);

        if (isEditingSavedMj) {
          const targetModel = (modelsData || []).find(m => m.id === activeSelection.id);
          if (targetModel) handleSelectSaved(targetModel);
        }
      } else {
        // 普通单能力模型
        const liblibExtra = vendorId === 'liblib-comfyui'
          ? {
              template_uuid: liblibTemplateUuid || '4df2efa0f18d46dc9758803e478eb51c',
              workflow_uuid: liblibWorkflowUuid || '',
              image_node_id: liblibImageNodeId || '',
              image_input_key: liblibImageInputKey || 'image',
              prompt_node_id: liblibPromptNodeId || '',
              prompt_input_key: liblibPromptInputKey || 'prompt',
              generate_params_template: liblibGenerateTemplate || ''
            }
          : {};

        // 将 LoRA 列表存入 enabledParams（在规范化之前）
        const paramsWithLora = {
          ...enabledParams,
          ...(loraList.length > 0 ? { lora: { loras: loraList } } : {})
        };

        const normalizedSingle = normalizeEnabledParamsForModel(paramsWithLora, currentModel, multipliers);

        // 🔀 多口交集：把启用参数、倍率收敛到"所有口都支持"的交集，并写入交集后的联动规则。
        //    保证用户端只看到交集选项，选出来的值发给任何口都合法。
        if (channelIntersection) {
          Object.entries(channelIntersection.optionsByParam).forEach(([key, allowed]) => {
            if (Array.isArray(normalizedSingle.enabledParameters[key])) {
              normalizedSingle.enabledParameters[key] = normalizedSingle.enabledParameters[key].filter(o => allowed.includes(o));
            }
            if (normalizedSingle.multipliers[key]) {
              const pruned = {};
              Object.entries(normalizedSingle.multipliers[key]).forEach(([o, v]) => { if (allowed.includes(o)) pruned[o] = v; });
              if (Object.keys(pruned).length > 0) normalizedSingle.multipliers[key] = pruned;
              else delete normalizedSingle.multipliers[key];
            }
          });
          if (Object.keys(channelIntersection.dependsOnByParam).length > 0) {
            normalizedSingle.enabledParameters._dependsOn = {
              ...(normalizedSingle.enabledParameters._dependsOn || {}),
              ...channelIntersection.dependsOnByParam
            };
          }
        }

        // 语言模型（剧本/分镜/文本润色的 LLM）：保存为 model_type='text'，进右侧「已添加模型」列表与图像/视频混排
        const selModelDef = currentVendor?.models?.find(m => m.id === selectedModel);
        const effectiveModelType = selModelDef?.isLanguageModel ? 'text' : modelType;

        const payload = {
          name: displayName || selectedModel,
          type: vendorId,
          model_type: effectiveModelType,
          api_url: baseUrl,
          api_key: finalApiKey,
          extra_json: JSON.stringify({
            model_id: selectedModel,
            ...liblibExtra,
            // 工具箱模型标记：后端 /api/toolbox-config 据此查询，前端据此显示工具条胶囊
            ...(selModelDef?.category === 'toolbox' ? { is_toolbox: true } : {})
          }),
          enabled_parameters: {
            ...normalizedSingle.enabledParameters,
            _multipliers: Object.keys(normalizedSingle.multipliers).length > 0 ? normalizedSingle.multipliers : undefined
          },
          credit_cost: creditCost,
          logo_url: logoUrl || null,
          description: description || null,
        };

        let savedId;
        // 判断当前保存的模型是否为工具箱模型（如 gpt_inpaint），决定是否跳过 model_mappings
        const isToolbox = currentVendor?.models?.find(m => m.id === selectedModel)?.category === 'toolbox';
        if (activeSelection.type === 'saved') {
          await api.put(`/api/admin/models/${activeSelection.id}`, payload);
          savedId = activeSelection.id;
        } else {
          const data = await api.post('/api/admin/add-model', payload);
          savedId = data.id;
          // 工具箱模型不加入 model_mappings → 不出现在模型选择器下拉列表中
          // 仅通过 /api/toolbox-config 接口暴露给前端工具条使用
          if (!isToolbox) {
            const currentMappings = await api.get('/api/admin/model-mappings');
            const currentIds = (currentMappings[modelType] || []).map(m => m.id);
            if (!currentIds.includes(savedId)) {
              await api.post('/api/admin/model-mappings', { media_type: modelType, modelIds: [...currentIds, savedId] });
            }
          }
        }

        // 保存多账号实例
        // 🛡️ 仅编辑已保存模型时才用面板里的多账号；新建模型忽略残留 modelInstances，只用本次填的 key
        const instancesToSave = (activeSelection.type === 'saved' && modelInstances.length > 0)
          ? modelInstances
          : (finalApiKey ? [{ label: '默认账号', api_key: finalApiKey, max_concurrency: 5 }] : []);
        if (instancesToSave.length > 0) {
          try { await api.post(`/api/admin/models/${savedId}/instances`, { instances: instancesToSave }); } catch (e) { console.warn('保存实例失败', e); }
        }

        // 刷新列表
        const modelsData = await api.get('/api/admin/models');
        setSavedModels(modelsData || []);
        window.dispatchEvent(new Event('modelsUpdated'));

        showToast(activeSelection.type === 'saved' ? '✅ 更新成功！' : '✅ 保存成功！', 'success', 3000);

        const targetModel = (modelsData || []).find(m => m.id === savedId);
        if (targetModel) handleSelectSaved(targetModel);
      }
    } catch (e) {
      showToast('❌ 保存失败：' + e.message, 'warning');
    } finally { setSaving(false); }
  };

  // ── 删除（隐藏）接口 ──
  // 接口是代码注册的，无法物理删除，这里软删除：写入后端 removed_providers 名单，列表过滤掉。
  // 已添加的模型不受影响，照常可用。
  const handleDeleteProvider = (vendorId, name) => {
    setConfirmModal({
      title: '确认删除接口',
      message: `确认删除接口：${name}？删除后它将从「可添加 API 接口」列表中移除。已添加的模型不受影响，仍可正常使用。`,
      danger: true,
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await api.delete(`/api/admin/providers/${encodeURIComponent(vendorId)}`);
          setRemovedProviders(prev => (prev.includes(vendorId) ? prev : [...prev, vendorId]));
          if (activeSelection?.type === 'provider' && activeSelection.vendorId === vendorId) { setActiveSelection(null); resetForm(); }
          showToast('✅ 接口已删除！', 'success', 3000);
        } catch (e) {
          showToast('❌ 删除失败：' + e.message, 'warning');
        }
      },
      onCancel: () => setConfirmModal(null)
    });
  };

  // ── 删除 ──
  const handleDelete = (id, name) => {
    setConfirmModal({
      title: '确认删除',
      message: `确认删除模型：${name}？此操作不可恢复。`,
      danger: true,
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await api.delete(`/api/admin/models/${id}`);
          const newModels = savedModels.filter(m => m.id !== id);
          setSavedModels(newModels);
          window.dispatchEvent(new Event('modelsUpdated'));
          showToast('✅ 删除成功！', 'success', 3000);
          if (activeSelection?.type === 'saved' && activeSelection.id === id) { setActiveSelection(null); resetForm(); }
        } catch (e) {
          showToast('❌ 删除失败：' + e.message, 'warning');
        }
      },
      onCancel: () => setConfirmModal(null)
    });
  };

  // ── 启用/禁用 ──
  const handleToggleEnabled = async (id, currentEnabled) => {
    try {
      const data = await api.patch(`/api/models/${id}/toggle`);
      if (data.success) {
        setSavedModels(prev => prev.map(m => m.id === id ? { ...m, enabled: data.enabled ? 1 : 0 } : m));
        window.dispatchEvent(new Event('modelsUpdated'));
      }
    } catch (e) { console.error('切换模型状态失败:', e); }
  };

  if (!open) return null;

  // ── 派生变量 ──
  // 根据 provider model 定义中的 category / isLanguageModel 字段分类
  const toolboxModelIds = new Set();
  const languageModelIds = new Set();
  providers.forEach(p => (p.models || []).forEach(m => {
    if (m.category === 'toolbox') toolboxModelIds.add(m.id);
    if (m.isLanguageModel) languageModelIds.add(m.id);
  }));
  const getModelId = (m) => {
    try { const extra = typeof m.extra_json === 'string' ? JSON.parse(m.extra_json) : (m.extra_json || {}); return extra.model_id || ''; } catch (e) { return ''; }
  };
  // 语言模型（豆包/Claude/DeepSeek/文本润色）：虽是 toolbox 注册，但归入「已添加模型」主列表（model_type='text'）
  const isLanguageModelRow = (m) => m.model_type === 'text' || languageModelIds.has(getModelId(m));
  // 仅图像类工具（GPT 局部重绘 / MJ 反推等）才进底部 🧰 面板
  const isToolboxModel = (m) => toolboxModelIds.has(getModelId(m)) && !isLanguageModelRow(m);
  const normalSavedModels = savedModels.filter(m => !isToolboxModel(m));
  const toolboxSavedModels = savedModels.filter(m => isToolboxModel(m));
  const hasToolbox = toolboxSavedModels.length > 0;

  const resolveVendorLabel = (vendorId, vendorName, fallback = '') => {
    const id = String(vendorId || '').toLowerCase();
    if (id.startsWith('yunwu') || id.startsWith('veo-')) return '云雾';
    if (id.startsWith('shuchuang')) return '速创';
    if (id.startsWith('apimart')) return 'ApiMart';
    return vendorName || fallback || vendorId || '';
  };

  const extractModelId = (model) => {
    if (!model) return '';
    if (model.model_id) return model.model_id;
    try {
      const extra = typeof model.extra_json === 'string' ? JSON.parse(model.extra_json) : (model.extra_json || {});
      return extra.model_id || '';
    } catch {
      return '';
    }
  };

  const resolveModelFamily = (modelId, modelName) => {
    const id = String(modelId || '').toLowerCase();
    if (id.startsWith('veo')) return 'VEO';
    if (id.startsWith('sora-2')) return 'Sora 2';
    if (id.startsWith('sora')) return 'Sora';
    if (id.startsWith('kling')) return 'Kling';
    if (id.includes('seedance')) return 'Seedance';
    if (id.startsWith('gpt-image-2') || id.startsWith('gptimage2')) return 'GPT-Image';
    if (id.startsWith('gpt-4o-image')) return 'GPT-4o';
    if (id.startsWith('grok')) return 'Grok';
    if (id.startsWith('nanobanana')) return 'NanoBanana';
    if (id.includes('hailuo')) return 'Hailuo';
    if (id.startsWith('gemini')) return 'Gemini';
    if (id.startsWith('mj_') || id.startsWith('midjourney')) return 'MJ';
    if (id.startsWith('jimeng')) return '即梦';
    if (id.startsWith('comfyui')) return 'ComfyUI';
    if (!modelName) return '';
    return String(modelName).replace(/\s+(pro|turbo|fast|all|vvip|omni|preview|beta|v\d+|\d+\.\d+|\d+)$/i, '').trim();
  };

  const resolveVendorWithFamily = (vendor, model) => {
    const vendorLabel = resolveVendorLabel(vendor?.vendorId || vendor?.type || model?.type || '', vendor?.vendorName, model?.type || '');
    const modelFamily = resolveModelFamily(extractModelId(model), model?.name);
    return modelFamily ? `${vendorLabel} ${modelFamily}` : vendorLabel;
  };

  // 从 provider 的 models 列表中提取共性模型族名，用于左侧标签显示
  const resolveProviderLabel = (provider) => {
    const vid = String(provider?.vendorId || '').toLowerCase();
    const vendorLabel = resolveVendorLabel(provider?.vendorId, provider?.vendorName);
    // 只对云雾/速创/ApiMart来源追加模型族名；其他厂商直接用 vendorName
    const isYunwu = vid.startsWith('yunwu') || vid.startsWith('veo-');
    const isShuchuang = vid.startsWith('shuchuang');
    const isApimart = vid.startsWith('apimart');
    if (!isYunwu && !isShuchuang && !isApimart) return vendorLabel;
    const models = provider?.models || [];
    if (models.length === 0) return vendorLabel;
    const firstModelId = models[0]?.id || '';
    const family = resolveModelFamily(firstModelId, models[0]?.name);
    const base = family ? `${vendorLabel} ${family}` : vendorLabel;
    // 同族多个接口时添加格式后缀做区分
    if (vid.includes('openai')) return `${base}（OpenAI）`;
    if (vid.includes('tongyi')) return `${base}（统一）`;
    if (vid === 'yunwu-sora2-all') return `${base} ALL`;
    return base;
  };

  const currentVendor = activeSelection?.type === 'provider'
    ? providers.find(p => p.vendorId === activeSelection.vendorId)
    : activeSelection?.type === 'saved'
    ? providers.find(p => p.vendorId === savedModels.find(m => m.id === activeSelection.id)?.type)
    : null;
  const currentModel = currentVendor?.models?.find(m => m.id === selectedModel);
  const isLiblibVendor = currentVendor?.vendorId === 'liblib-comfyui';

  // 🔀 多口交集：当同一模型的多个口指向不同上游模型（各自支持的分辨率/比例不同）时，
  //    只暴露所有口都支持的选项（交集），保证选出来的值发给任何口都能用。
  //    返回 null 表示无需限制（单一上游，或无法判定）。
  const computeChannelIntersection = () => {
    const vendor = currentVendor;
    if (!vendor?.models || !Array.isArray(modelInstances) || modelInstances.length === 0) return null;
    const defs = [];
    const unknown = [];
    modelInstances.forEach(inst => {
      // 只统计"会被实际使用"的口：禁用的口不参与交集（它不会被 failover 选中）
      const enabled = inst.enabled === undefined || inst.enabled === 1 || inst.enabled === true;
      if (!enabled) return;
      const upName = ((inst.upstream_model || '').trim()) || selectedModel;
      const def = vendor.models.find(m => m.id === upName);
      if (def) defs.push(def);
      else if (upName) unknown.push(upName);
    });
    const uniqDefs = [...new Map(defs.map(d => [d.id, d])).values()];
    if (uniqDefs.length < 2) return null; // 单一上游（或都匹配同一定义），无需求交集
    const optionsByParam = {};
    const dependsOnByParam = {};
    const baseParams = uniqDefs[0].parameters || {};
    Object.entries(baseParams).forEach(([key, p]) => {
      if (!(p.type === 'select' && Array.isArray(p.options))) return;
      // options 交集：某个口没有该参数则不纳入限制（保守，避免误砍）
      let inter = [...p.options];
      let allHave = true;
      for (const d of uniqDefs) {
        const dp = d.parameters?.[key];
        if (!dp || !Array.isArray(dp.options)) { allHave = false; break; }
        inter = inter.filter(o => dp.options.includes(o));
      }
      if (!allHave) return;
      optionsByParam[key] = inter;
      // dependsOn（联动）交集：逐父值取子选项交集；无 dependsOn 的口视为该父值下全支持
      const field = uniqDefs.map(d => d.parameters?.[key]?.dependsOn?.field).find(Boolean);
      if (field) {
        const parentInter = optionsByParam[field] || baseParams[field]?.options || [];
        const validOptions = {};
        parentInter.forEach(parentVal => {
          let sizes = [...inter];
          for (const d of uniqDefs) {
            const dep = d.parameters?.[key]?.dependsOn;
            const allowed = dep ? (dep.validOptions?.[parentVal] || []) : (d.parameters?.[key]?.options || []);
            sizes = sizes.filter(s => allowed.includes(s));
          }
          validOptions[parentVal] = sizes;
        });
        dependsOnByParam[key] = { field, validOptions };
      }
    });
    if (Object.keys(optionsByParam).length === 0) return null;
    return { optionsByParam, dependsOnByParam, unknown };
  };
  const channelIntersection = computeChannelIntersection();

  const isSaveDisabled = (() => {
    if (saving || !selectedModel) return true;
    const vendorId = activeSelection?.type === 'provider'
      ? activeSelection.vendorId
      : savedModels.find(m => m.id === activeSelection.id)?.type;
    if (activeSelection?.type === 'saved') {
      if (modelInstances.length === 0) return true;
      if (vendorId === 'jimeng-volcengine') {
        return modelInstances.every(inst => { const parts = (inst.api_key || '').split(','); return !parts[0]?.trim() || !parts[1]?.trim(); });
      }
      return modelInstances.every(inst => !inst.api_key?.trim());
    }
    if (vendorId === 'jimeng-volcengine') return !volcAccessKeyId || !volcSecretAccessKey;
    return !apiKey;
  })();

  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  //  渲染
  // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
  const modalContent = (
    <div style={{...overlay, pointerEvents: 'auto'}}>
      <div ref={modalRef} style={modalStyle}>
        {/* Header */}
        <div style={header}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Cloud size={14} /> API 配置</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', transition: 'color 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.color = 'var(--text-hover)'} onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-secondary)'}>
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div style={bodyStyle}>
          {loading ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>加载中...</div>
          ) : (
            <>
              {/* ── 左侧面板：可添加 API 接口 + 工具箱 ── */}
              <div style={leftPanel}>
                {/* 可添加 API 接口 */}
                <div style={{ padding: 12, borderBottom: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', flex: hasToolbox ? 0.7 : 1, minHeight: 0 }}>
                  <label style={{ ...labelStyle, marginBottom: 8 }}>可添加 API 接口</label>
                  <div ref={providersListRef} className="custom-scrollbar" style={{ display: 'flex', flexDirection: 'column', gap: 4, overflowY: 'auto', flex: 1, minHeight: 0 }} onPointerMove={handleProviderPointerMove} onPointerUp={handleProviderPointerUp}>
                    {providers.filter(p => !removedProviders.includes(p.vendorId)).map((p) => {
                      const isActive = activeSelection?.type === 'provider' && activeSelection.vendorId === p.vendorId;
                      const isDragging = dragProviderId === p.vendorId;
                      const isDropTarget = dropProviderTargetId === p.vendorId && dragProviderId && dragProviderId !== p.vendorId;
                      return (
                        <div key={p.vendorId} data-provider-id={p.vendorId} style={{ position: 'relative' }}>
                          <button
                            onPointerDown={(e) => handleProviderPointerDown(e, p.vendorId)}
                            onClick={() => handleSelectProvider(p.vendorId)}
                            style={{
                              width: '100%', padding: '8px 32px 8px 10px',
                              background: isActive ? 'rgba(234,179,8,0.2)' : 'var(--modal-input-bg)',
                              border: `1px solid ${isActive ? '#eab308' : 'rgba(255,255,255,0.12)'}`,
                              borderTop: isDropTarget ? '2px solid #3b82f6' : undefined,
                              borderRadius: 6, cursor: dragProviderId ? 'grabbing' : 'grab',
                              transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 8,
                              fontSize: 12, color: isActive ? '#fbbf24' : 'var(--text-secondary)',
                              fontWeight: isActive ? 600 : 400, textAlign: 'left',
                              opacity: isDragging ? 0.4 : 1, touchAction: 'none'
                            }}
                            onMouseEnter={(e) => { if (!isActive && !dragProviderId) { e.currentTarget.style.background = 'var(--modal-input-border)'; e.currentTarget.style.color = 'var(--text-body)'; } }}
                            onMouseLeave={(e) => { if (!isActive && !dragProviderId) { e.currentTarget.style.background = 'var(--modal-input-bg)'; e.currentTarget.style.color = 'var(--text-secondary)'; } }}
                          >
                            <Cloud size={14} color={isActive ? '#fbbf24' : '#94a3b8'} />
                            {resolveProviderLabel(p)}
                          </button>
                          {/* 🗑️ 删除接口按钮（软删除，不影响已添加模型） */}
                          <button
                            data-provider-del
                            onPointerDown={(e) => { e.stopPropagation(); }}
                            onClick={(e) => { e.stopPropagation(); handleDeleteProvider(p.vendorId, resolveProviderLabel(p)); }}
                            title="删除该接口"
                            style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', width: 22, height: 22, padding: 0, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', borderRadius: 4, transition: 'all 0.15s', zIndex: 1 }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(239,68,68,0.15)'; e.currentTarget.style.color = '#f87171'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#64748b'; }}
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* 🧰 工具箱区域（从已保存模型中筛选） */}
                {toolboxSavedModels.length > 0 && (
                  <div style={{ padding: 12, borderTop: '1px solid rgba(255,255,255,0.08)', flex: 0.3, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    <div style={{ fontSize: 11, color: '#b8860b', marginBottom: 8, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                      <Wrench size={12} /> 工具箱
                    </div>
                    <div className="custom-scrollbar" style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                      {toolboxSavedModels.map(m => {
                        const isActive = activeSelection?.type === 'saved' && activeSelection.id === m.id;
                        const vendor = providers.find(p => p.vendorId === m.type);
                        const isEnabled = m.enabled !== 0;
                        return (
                          <div key={m.id} onClick={() => handleSelectSaved(m)}
                            style={{
                              padding: '8px 10px', paddingRight: 32,
                              background: isActive ? 'rgba(184,134,11,0.2)' : 'var(--modal-input-bg)',
                              border: `1px solid ${isActive ? '#b8860b' : 'transparent'}`,
                              borderRadius: 6, cursor: 'pointer', transition: 'all 0.15s',
                              position: 'relative', marginBottom: 4,
                            }}
                            onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'var(--modal-input-border)'; }}
                            onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'var(--modal-input-bg)'; }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              {m.logo_url ? (
                                <img src={m.logo_url} alt={m.name} style={{ width: 16, height: 16, objectFit: 'contain', borderRadius: 3 }}
                                  onError={e => { e.target.style.display = 'none'; }} />
                              ) : (
                                <Wrench size={16} color={isActive ? '#daa520' : '#94a3b8'} />
                              )}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 12, color: isActive ? 'var(--text-body)' : 'var(--text-secondary)', fontWeight: isActive ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {m.name}
                                </div>
                                <div style={{ fontSize: 9, color: '#64748b', marginTop: 2 }}>
                                  工具箱 • {resolveVendorWithFamily(vendor, m)}
                                </div>
                              </div>
                            </div>
                            <button data-model-btn onClick={(e) => { e.stopPropagation(); handleToggleEnabled(m.id, isEnabled); }}
                              style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', width: 20, height: 20, padding: 0, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', zIndex: 1 }}
                              title={isEnabled ? '点击禁用' : '点击启用'}>
                              {isEnabled ? <CheckCircle2 size={20} color="#10b981" /> : <Circle size={20} color="#6b7280" />}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* 🎬 三大语言模型功能设定入口（图标 / Skills / 选择模型，全局，与具体模型解耦）
                    建模大师 → 分镜大师 → 文本润色 → 预设工具箱 */}
                <div style={{ padding: 12, borderTop: '1px solid rgba(255,255,255,0.08)', flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <button onClick={() => setModelMasterModalOpen(true)}
                    style={{ width: '100%', padding: '10px', background: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.3)', borderRadius: 6, color: '#a78bfa', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, transition: 'all 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(167,139,250,0.2)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(167,139,250,0.12)'; }}>
                    <ScrollText size={14} /> 建模大师设定
                  </button>
                  <button onClick={() => setStoryboardModalOpen(true)}
                    style={{ width: '100%', padding: '10px', background: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.3)', borderRadius: 6, color: '#60a5fa', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, transition: 'all 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(96,165,250,0.2)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(96,165,250,0.12)'; }}>
                    <Wand2 size={14} /> 分镜大师设定
                  </button>
                  <button onClick={() => setTextPolishModalOpen(true)}
                    style={{ width: '100%', padding: '10px', background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.3)', borderRadius: 6, color: '#34d399', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, transition: 'all 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(52,211,153,0.2)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(52,211,153,0.12)'; }}>
                    <Wand2 size={14} /> 文本润色设定
                  </button>
                  <button onClick={() => setPresetModalOpen(true)}
                    style={{ width: '100%', padding: '10px', background: 'rgba(218,165,32,0.12)', border: '1px solid rgba(218,165,32,0.3)', borderRadius: 6, color: '#daa520', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, transition: 'all 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(218,165,32,0.2)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(218,165,32,0.12)'; }}>
                    <Wrench size={14} /> 预设工具箱
                  </button>
                  <button onClick={() => setSkillboxModalOpen(true)}
                    style={{ width: '100%', padding: '10px', background: 'rgba(139,92,246,0.12)', border: '1px solid rgba(139,92,246,0.3)', borderRadius: 6, color: '#8b5cf6', fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, transition: 'all 0.15s' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'rgba(139,92,246,0.2)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'rgba(139,92,246,0.12)'; }}>
                    <Sparkles size={14} /> 技能工具箱
                  </button>
                </div>
              </div>

              {/* ── 中列面板：已添加模型 ── */}
              <div style={middlePanel}>
                <div style={{ padding: 12, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>已添加模型</div>
                </div>
                <div className="custom-scrollbar" style={{ padding: 12, flex: 1, overflowY: 'auto' }}>
                  {savedModels.length === 0 ? (
                    <div style={{ fontSize: 11, color: '#475569', textAlign: 'center', padding: '20px 0' }}>暂无</div>
                  ) : (
                    <div ref={savedModelsListRef} style={{ display: 'flex', flexDirection: 'column', gap: 4 }} onPointerMove={handleModelPointerMove} onPointerUp={handleModelPointerUp}>
                      {normalSavedModels.map((m) => {
                        const isActive = activeSelection?.type === 'saved' && activeSelection.id === m.id;
                        const vendor = providers.find(p => p.vendorId === m.type);
                        const isEnabled = m.enabled !== 0;
                        const isDragging = String(dragModelId) === String(m.id);
                        const isDropTarget = String(dropTargetId) === String(m.id) && dragModelId && String(dragModelId) !== String(m.id);
                        return (
                          <div key={m.id} data-model-id={m.id} onPointerDown={(e) => handleModelPointerDown(e, m.id)} style={{
                            padding: '8px 10px', paddingRight: 32,
                            background: isActive ? 'rgba(59,130,246,0.2)' : 'var(--modal-input-bg)',
                            border: `1px solid ${isActive ? '#3b82f6' : 'transparent'}`,
                            borderTop: isDropTarget ? '2px solid #3b82f6' : undefined,
                            borderRadius: 6, cursor: dragModelId ? 'grabbing' : 'grab',
                            transition: 'all 0.15s', position: 'relative',
                            opacity: isDragging ? 0.4 : 1, touchAction: 'none'
                          }}
                            onMouseEnter={(e) => { if (!isActive && !dragModelId) e.currentTarget.style.background = 'var(--modal-input-border)'; }}
                            onMouseLeave={(e) => { if (!isActive && !dragModelId) e.currentTarget.style.background = 'var(--modal-input-bg)'; }}
                          >
                            <div onClick={() => handleSelectSaved(m)} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              {m.logo_url ? (
                                <img src={m.logo_url} alt={resolveVendorLabel(vendor?.vendorId || vendor?.type || m.type, vendor?.vendorName, m.type)} style={{ width: 16, height: 16, objectFit: 'contain', borderRadius: 3 }}
                                  onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'inline-block'; }} />
                              ) : null}
                              <Box size={16} color={isActive ? '#60a5fa' : '#94a3b8'} style={{ display: m.logo_url ? 'none' : 'inline-block' }} />
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 12, color: isActive ? 'var(--text-body)' : 'var(--text-secondary)', fontWeight: isActive ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name}</div>
                                <div style={{ fontSize: 9, color: '#64748b', marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <span>{m.model_type === 'image' ? '图像' : m.model_type === 'text' ? '语言' : '视频'}</span><span>•</span><span>{resolveVendorWithFamily(vendor, m)}</span>
                                </div>
                              </div>
                            </div>
                            <button data-model-btn onClick={(e) => { e.stopPropagation(); handleToggleEnabled(m.id, isEnabled); }}
                              style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', width: 20, height: 20, padding: 0, background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s', zIndex: 1 }}
                              title={isEnabled ? '点击禁用' : '点击启用'}>
                              {isEnabled ? <CheckCircle2 size={20} color="#10b981" /> : <Circle size={20} color="#6b7280" />}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* ── 右侧面板 ── */}
              <div className="custom-scrollbar" style={rightPanel}>
                {!activeSelection ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#64748b' }}>
                    <Cloud size={48} color="#475569" style={{ marginBottom: 16 }} />
                    <p style={{ margin: 0 }}>← 请在左侧或中列选择接口或模型</p>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* 标题 */}
                    <div style={{ paddingBottom: 12, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--text-body)' }}>
                        {activeSelection.type === 'provider' ? '添加新模型' : '编辑模型配置'}
                      </h3>
                      <p style={{ margin: '4px 0 0', fontSize: 16, color: '#3b82f6' }}>
                        {activeSelection.type === 'provider'
                          ? `接口：${resolveProviderLabel(currentVendor)}`
                          : (() => {
                              const vendor = providers.find(p => p.vendorId === savedModels.find(m => m.id === activeSelection.id)?.type);
                              return `ID: ${activeSelection.id} | ${modelType === 'image' ? '图像' : '视频'} · ${resolveProviderLabel(vendor)}`;
                            })()}
                      </p>
                    </div>

                    {/* ── 多账号管理（仅已保存模型） ── */}
                    {activeSelection.type === 'saved' && (
                      <div>
                        {modelInstances.length > 0 && (
                          <div style={{ marginBottom: 10, padding: '6px 10px', borderRadius: 6, background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.15)', display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: '#93c5fd' }}>
                            <span>已配置 <b style={{ color: '#bfdbfe' }}>{modelInstances.length}</b> 个账号</span>
                            <span style={{ color: 'var(--border-strong)' }}>|</span>
                            <span>总并发 <b style={{ color: '#bfdbfe' }}>{modelInstances.some(i => (i.max_concurrency ?? 5) === 0) ? '∞ (无限制)' : modelInstances.reduce((s, i) => s + (Number(i.max_concurrency) || 5), 0)}</b></span>
                          </div>
                        )}
                        <label style={labelStyle}>API 账号</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <button onClick={() => { setModelInstances(prev => [...prev, { label: '', api_key: '', max_concurrency: 5 }]); setSelectedInstanceIdx(modelInstances.length); setShowInstanceKey(false); }}
                            title="添加账号" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 6, flexShrink: 0, border: '1px solid rgba(59,130,246,0.3)', background: 'rgba(59,130,246,0.1)', color: '#60a5fa', cursor: 'pointer', transition: 'all 0.15s' }}
                            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(59,130,246,0.2)'; }} onMouseLeave={e => { e.currentTarget.style.background = 'rgba(59,130,246,0.1)'; }}>
                            <Plus size={16} />
                          </button>
                          <select value={selectedInstanceIdx} onChange={e => { setSelectedInstanceIdx(Number(e.target.value)); setShowInstanceKey(false); }}
                            style={{ ...inputStyle, flex: 1, cursor: 'pointer', appearance: 'auto', WebkitAppearance: 'auto' }} disabled={modelInstances.length === 0}>
                            {instancesLoading && <option>加载中...</option>}
                            {!instancesLoading && modelInstances.length === 0 && <option>暂无账号，请点击 + 添加</option>}
                            {modelInstances.map((inst, i) => <option key={i} value={i}>{`${i + 1}. `}{inst.label || `账号 ${i + 1}`}{inst.api_key ? ` (${inst.api_key.substring(0, 8)}...)` : ''}</option>)}
                          </select>
                          {modelInstances.length > 1 && (() => {
                            const curIdx = Math.min(selectedInstanceIdx, modelInstances.length - 1);
                            const swap = (to) => {
                              if (to < 0 || to >= modelInstances.length) return;
                              setModelInstances(prev => { const a = [...prev]; [a[curIdx], a[to]] = [a[to], a[curIdx]]; return a; });
                              setSelectedInstanceIdx(to);
                            };
                            const arrowBtn = { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 26, height: 32, borderRadius: 6, flexShrink: 0, border: '1px solid rgba(255,255,255,0.08)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 14, lineHeight: 1 };
                            return (
                              <>
                                <button onClick={() => swap(curIdx - 1)} disabled={curIdx === 0} title="上移（提高优先级，越靠前越先用，失败再切下一个）"
                                  style={{ ...arrowBtn, opacity: curIdx === 0 ? 0.3 : 1, cursor: curIdx === 0 ? 'not-allowed' : 'pointer' }}>↑</button>
                                <button onClick={() => swap(curIdx + 1)} disabled={curIdx === modelInstances.length - 1} title="下移（降低优先级）"
                                  style={{ ...arrowBtn, opacity: curIdx === modelInstances.length - 1 ? 0.3 : 1, cursor: curIdx === modelInstances.length - 1 ? 'not-allowed' : 'pointer' }}>↓</button>
                              </>
                            );
                          })()}
                          {modelInstances.length > 0 && (
                            <button onClick={() => { setModelInstances(prev => prev.filter((_, idx) => idx !== selectedInstanceIdx)); setSelectedInstanceIdx(prev => Math.max(0, Math.min(prev, modelInstances.length - 2))); setShowInstanceKey(false); }}
                              title="删除当前账号" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: 6, flexShrink: 0, border: '1px solid rgba(255,255,255,0.08)', background: 'transparent', color: '#64748b', cursor: 'pointer', transition: 'all 0.15s' }}
                              onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.3)'; }} onMouseLeave={e => { e.currentTarget.style.color = '#64748b'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; }}>
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>

                        {/* 账号配置表单 */}
                        {modelInstances.length > 0 && (() => {
                          const idx = Math.min(selectedInstanceIdx, modelInstances.length - 1);
                          const inst = modelInstances[idx];
                          if (!inst) return null;
                          const update = (field, value) => setModelInstances(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
                          const vendorId = savedModels.find(m => m.id === activeSelection.id)?.type;
                          const isVolc = vendorId === 'jimeng-volcengine';
                          const volcKeyParts = isVolc && inst.api_key ? inst.api_key.split(',') : ['', ''];
                          return (
                            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 10, padding: 14, background: 'var(--modal-input-bg)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8 }}>
                              <div>
                                <label style={{ fontSize: 11, color: '#64748b', marginBottom: 2, display: 'block' }}>账号名称</label>
                                <input style={inputStyle} value={inst.label || ''} onChange={e => update('label', e.target.value)} placeholder={`账号 ${idx + 1}`} />
                              </div>
                              {isVolc ? (
                                <>
                                  <div>
                                    <label style={{ fontSize: 11, color: '#64748b', marginBottom: 2, display: 'block' }}>Access Key ID</label>
                                    <div style={{ position: 'relative' }}>
                                      <input style={{ ...inputStyle, paddingRight: 36 }} type={showInstanceKey ? 'text' : 'password'} value={volcKeyParts[0] || ''} onChange={e => update('api_key', `${e.target.value},${volcKeyParts[1] || ''}`)} placeholder="AKLT开头的Access Key ID" />
                                      <button onClick={() => setShowInstanceKey(!showInstanceKey)} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                        {showInstanceKey ? <EyeOff size={14} /> : <Eye size={14} />}
                                      </button>
                                    </div>
                                  </div>
                                  <div>
                                    <label style={{ fontSize: 11, color: '#64748b', marginBottom: 2, display: 'block' }}>Secret Access Key</label>
                                    <div style={{ position: 'relative' }}>
                                      <input style={{ ...inputStyle, paddingRight: 36 }} type={showInstanceKey ? 'text' : 'password'} value={volcKeyParts[1] || ''} onChange={e => update('api_key', `${volcKeyParts[0] || ''},${e.target.value}`)} placeholder="Base64编码的Secret Key" />
                                    </div>
                                  </div>
                                </>
                              ) : (
                                <div>
                                  <label style={{ fontSize: 11, color: '#64748b', marginBottom: 2, display: 'block' }}>API Key</label>
                                  <div style={{ position: 'relative' }}>
                                    <input style={{ ...inputStyle, paddingRight: 36 }} type={showInstanceKey ? 'text' : 'password'} value={inst.api_key || ''} onChange={e => update('api_key', e.target.value)} placeholder="sk-..." />
                                    <button onClick={() => setShowInstanceKey(!showInstanceKey)} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                      {showInstanceKey ? <EyeOff size={14} /> : <Eye size={14} />}
                                    </button>
                                  </div>
                                </div>
                              )}
                              <div>
                                <label style={{ fontSize: 11, color: '#64748b', marginBottom: 2, display: 'block' }}>最大并发数</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                  <input type="number" min="0" style={{ ...inputStyle, width: 100 }} value={(inst.max_concurrency ?? 5) === 0 ? '' : (inst.max_concurrency ?? 5)} onChange={e => { const v = e.target.value === '' ? 0 : Math.max(0, parseInt(e.target.value) || 0); update('max_concurrency', v); }} placeholder="不限制" disabled={(inst.max_concurrency ?? 5) === 0} />
                                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--text-secondary)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                    <input type="checkbox" checked={(inst.max_concurrency ?? 5) === 0} onChange={e => update('max_concurrency', e.target.checked ? 0 : 5)} style={{ accentColor: '#3b82f6' }} />
                                    不限制
                                  </label>
                                </div>
                                <div style={{ fontSize: 11, color: '#475569', marginTop: 3 }}>{(inst.max_concurrency ?? 5) === 0 ? '该账号不限制并发数量' : '该账号允许同时进行的生成任务数'}</div>
                              </div>
                              <div>
                                <label style={{ fontSize: 11, color: '#64748b', marginBottom: 2, display: 'block' }}>上游模型名（可选）</label>
                                <input style={inputStyle} value={inst.upstream_model || ''} onChange={e => update('upstream_model', e.target.value)} placeholder="留空=用模型默认；如 gpt-image-2-official" />
                                <div style={{ fontSize: 11, color: '#475569', marginTop: 3 }}>该口调用的真实上游模型名。多口时越靠前越先用，失败自动切下一个口（如逆向失败切官转）。</div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}

                    {/* ── API 接口地址 ── */}
                    <div>
                      <label style={labelStyle}>API 接口地址</label>
                      <input style={inputStyle} value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://cccodex.cn" />
                    </div>

                    {/* ── API Key（仅新建模型） ── */}
                    {activeSelection.type === 'provider' && (() => {
                      const vendorId = activeSelection.vendorId;
                      if (vendorId === 'jimeng-volcengine') {
                        return (
                          <>
                            <div>
                              <label style={labelStyle}>Access Key ID</label>
                              <div style={{ position: 'relative' }}>
                                <input style={{ ...inputStyle, paddingRight: 36 }} type={showVolcKeyId ? 'text' : 'password'} value={volcAccessKeyId} onChange={(e) => setVolcAccessKeyId(e.target.value)} placeholder="AKLT开头的Access Key ID" />
                                <button onClick={() => setShowVolcKeyId(!showVolcKeyId)} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                  {showVolcKeyId ? <EyeOff size={14} /> : <Eye size={14} />}
                                </button>
                              </div>
                            </div>
                            <div>
                              <label style={labelStyle}>Secret Access Key</label>
                              <div style={{ position: 'relative' }}>
                                <input style={{ ...inputStyle, paddingRight: 36 }} type={showVolcKeySecret ? 'text' : 'password'} value={volcSecretAccessKey} onChange={(e) => setVolcSecretAccessKey(e.target.value)} placeholder="Base64编码的Secret Key" />
                                <button onClick={() => setShowVolcKeySecret(!showVolcKeySecret)} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                  {showVolcKeySecret ? <EyeOff size={14} /> : <Eye size={14} />}
                                </button>
                              </div>
                            </div>
                          </>
                        );
                      }
                      return (
                        <div>
                          <label style={labelStyle}>API Key</label>
                          <div style={{ position: 'relative' }}>
                            <input
                              style={{ ...inputStyle, paddingRight: 36 }}
                              type={showKey ? 'text' : 'password'}
                              value={apiKey}
                              onChange={(e) => setApiKey(e.target.value)}
                              placeholder={vendorId === 'liblib-comfyui' ? 'AccessKey,SecretKey' : '输入 API Key'}
                            />
                            <button onClick={() => setShowKey(!showKey)} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                              {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
                            </button>
                          </div>
                        </div>
                      );
                    })()}

                    {/* ── 模型选择（新建时） ── */}
                    {activeSelection.type === 'provider' && currentVendor && (
                      <div>
                        <label style={labelStyle}>选择模型</label>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8 }}>
                          {currentVendor.models?.map(m => {
                            const isActive = selectedModel === m.id;
                            return (
                              <button key={m.id} onClick={() => handleModelChange(m.id)}
                                style={{ padding: '10px 12px', background: isActive ? 'rgba(59,130,246,0.2)' : 'var(--modal-input-bg)', border: `1px solid ${isActive ? '#3b82f6' : 'rgba(255,255,255,0.12)'}`, borderRadius: 6, cursor: 'pointer', transition: 'all 0.15s', fontSize: 12, color: isActive ? '#60a5fa' : 'var(--text-secondary)', fontWeight: isActive ? 600 : 400, textAlign: 'left' }}
                                onMouseEnter={(e) => { if (!isActive) { e.currentTarget.style.background = 'var(--modal-input-border)'; e.currentTarget.style.color = 'var(--text-body)'; } }}
                                onMouseLeave={(e) => { if (!isActive) { e.currentTarget.style.background = 'var(--modal-input-bg)'; e.currentTarget.style.color = 'var(--text-secondary)'; } }}>
                                {m.name || m.id}
                              </button>
                            );
                          })}
                        </div>
                        {selectedModel && currentModel?.category !== 'toolbox' && (
                          <div style={{ marginTop: 12 }}>
                            <label style={{ fontSize: 11, color: '#64748b', marginBottom: 4, display: 'block' }}>真实模型名（可修改）</label>
                            <input style={inputStyle} value={selectedModel} onChange={(e) => setSelectedModel(e.target.value)} placeholder="API调用的真实模型名" />
                          </div>
                        )}
                      </div>
                    )}
                    {/* ── 真实模型名（编辑已保存模型） ── */}
                    {activeSelection.type === 'saved' && currentModel?.category !== 'toolbox' && (
                      <div>
                        <label style={labelStyle}>真实模型名（可修改）</label>
                        <input style={inputStyle} value={selectedModel} onChange={(e) => setSelectedModel(e.target.value)} placeholder="API调用的真实模型名" />
                      </div>
                    )}

                    {/* ── Liblib ComfyUI：工作流模板配置（支持任意工作流） ── */}
                    {isLiblibVendor && (
                      <div style={{ padding: 12, borderRadius: 8, background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)' }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: '#93c5fd', marginBottom: 10 }}>Liblib 工作流模板配置</div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                          <div>
                            <label style={labelStyle}>templateUuid</label>
                            <input style={inputStyle} value={liblibTemplateUuid} onChange={(e) => setLiblibTemplateUuid(e.target.value)} placeholder="模板 UUID" />
                          </div>
                          <div>
                            <label style={labelStyle}>workflowUuid</label>
                            <input style={inputStyle} value={liblibWorkflowUuid} onChange={(e) => setLiblibWorkflowUuid(e.target.value)} placeholder="工作流 UUID" />
                          </div>
                          <div>
                            <label style={labelStyle}>图片节点 ID</label>
                            <input style={inputStyle} value={liblibImageNodeId} onChange={(e) => setLiblibImageNodeId(e.target.value)} placeholder="如 62" />
                          </div>
                          <div>
                            <label style={labelStyle}>图片输入键</label>
                            <input style={inputStyle} value={liblibImageInputKey} onChange={(e) => setLiblibImageInputKey(e.target.value)} placeholder="image" />
                          </div>
                          <div>
                            <label style={labelStyle}>提示词节点 ID（可选）</label>
                            <input style={inputStyle} value={liblibPromptNodeId} onChange={(e) => setLiblibPromptNodeId(e.target.value)} placeholder="如 76" />
                          </div>
                          <div>
                            <label style={labelStyle}>提示词输入键</label>
                            <input style={inputStyle} value={liblibPromptInputKey} onChange={(e) => setLiblibPromptInputKey(e.target.value)} placeholder="prompt1 / prompt" />
                          </div>
                        </div>

                        <div style={{ marginTop: 10 }}>
                          <label style={labelStyle}>generateParams 模板 JSON（可选，推荐填）</label>
                          <textarea
                            style={{ ...inputStyle, minHeight: 140, resize: 'vertical', fontFamily: 'monospace', lineHeight: 1.5 }}
                            value={liblibGenerateTemplate}
                            onChange={(e) => setLiblibGenerateTemplate(e.target.value)}
                            placeholder={'粘贴 Lib 工作流 API 示例里的 generateParams 对象 JSON。\n可用占位符：{{imageUrl}} {{prompt}}'}
                          />
                          <div style={{ marginTop: 6, fontSize: 11, color: 'var(--text-secondary)' }}>
                            说明：保存后将绑定到该模型。生成时会自动注入参考图 URL 和提示词，适配不同工作流节点。
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ── 通用模型配置（Midjourney 专用模式下隐藏，避免重复） ── */}
                    {!(activeSelection.type === 'provider' && activeSelection.vendorId === 'yunwu-midjourney') && (
                      <>
                        {/* ── 模型 Logo ── */}
                        <div>
                          <label style={labelStyle}>模型 Logo（可选）</label>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <div style={{ width: 48, height: 48, borderRadius: 8, background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0, position: 'relative' }}>
                              {logoUrl && !logoLoadError && <img src={logoUrl} onError={() => setLogoLoadError(true)} onLoad={() => setLogoLoadError(false)} style={{ width: '100%', height: '100%', objectFit: 'contain' }} alt="Logo" />}
                              <Sparkles size={20} style={{ color: '#64748b', opacity: 0.5, display: (logoUrl && !logoLoadError) ? 'none' : 'flex' }} />
                            </div>
                            <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" style={{ display: 'none' }} id="logo-upload-provider"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                setLogoUploading(true);
                                try {
                                  const buffer = await file.arrayBuffer();
                                  // 拿不到哈希（http 非安全上下文）就跳过去重预检，直接上传
                                  const hashHex = await sha256Hex(buffer);
                                  if (hashHex) {
                                    const checkResult = await api.get(`/api/upload/check-hash?hash=${hashHex}&type=logo`);
                                    if (checkResult.exists && checkResult.url) { setLogoUrl(checkResult.url); setLogoLoadError(false); setLogoUploading(false); e.target.value = ''; return; }
                                  }
                                  const formData = new FormData(); formData.append('file', file);
                                  const { getToken: getAuthToken } = await import('../api');
                                  const result = await fetch(`${C.API_BASE_URL}/api/upload?type=logo`, { method: 'POST', headers: { 'Authorization': `Bearer ${getAuthToken()}` }, body: formData }).then(r => r.json());
                                  if (result.url) { setLogoUrl(result.url); setLogoLoadError(false); }
                                  else if (result.error) { showToast('❌ 上传失败: ' + result.error, 'warning'); }
                                } catch (err) { 
                                  console.error('上传 Logo 失败:', err); 
                                  showToast('❌ 上传失败', 'warning'); 
                                }
                                setLogoUploading(false); 
                                e.target.value = '';
                              }} />
                            <label htmlFor="logo-upload-provider" style={{ padding: '8px 16px', background: logoUploading ? 'rgba(59,130,246,0.3)' : 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 6, color: '#60a5fa', fontSize: 13, cursor: logoUploading ? 'not-allowed' : 'pointer', userSelect: 'none', transition: 'all 0.2s' }}
                              onMouseEnter={e => { if(!logoUploading) e.currentTarget.style.background = 'rgba(59,130,246,0.25)'; }} onMouseLeave={e => { if(!logoUploading) e.currentTarget.style.background = 'rgba(59,130,246,0.15)'; }}>
                              {logoUploading ? '上传中...' : '上传 Logo'}
                            </label>
                            {logoUrl && <button onClick={() => { setLogoUrl(''); setLogoLoadError(false); }} style={{ padding: '8px 12px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, color: '#f87171', fontSize: 13, cursor: 'pointer' }}>清除</button>}
                          </div>
                          <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>支持 PNG、JPG、SVG、WebP 格式，建议尺寸 48x48 或更大</div>
                        </div>
                      </>
                    )}

                    {/* ── Midjourney 多能力模型配置（特殊处理） ── */}
                    {activeSelection.type === 'provider' && activeSelection.vendorId === 'yunwu-midjourney' && (
                      <div>
                        <div style={{ marginBottom: 20, padding: '12px 16px', background: 'linear-gradient(135deg, rgba(59,130,246,0.15), rgba(147,51,234,0.15))', borderRadius: 8, border: '1px solid rgba(59,130,246,0.3)' }}>
                          <h3 style={{ margin: '0 0 8px 0', fontSize: 14, fontWeight: 600, color: '#60a5fa', display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Zap size={16} />
                            Midjourney 多合一配置
                          </h3>
                          <p style={{ margin: 0, fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                            请选择上方某个 Midjourney 能力模型进行单独配置。
                            保存时仅保存当前选中的能力模型。
                          </p>
                        </div>

                        {currentVendor.models?.filter(m => m.id === selectedModel).map((model, idx) => {
                          const modelEnabledParams = mjEnabledParams[model.id] || {};
                          const modelMultipliers = mjMultipliers[model.id] || {};
                          const modelCreditCost = mjCreditCosts[model.id] ?? model.creditCost ?? 0;
                          const modelDisplayName = mjDisplayNames[model.id] || '';
                          const modelDescription = mjDescriptions[model.id] || '';
                          const modelRealName = mjRealNames[model.id] || model.id;

                          return (
                            <div key={model.id} style={{ marginBottom: 24, paddingBottom: 24, borderBottom: idx < (currentVendor.models?.length || 0) - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>
                              {/* 模型标题 */}
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                                <div style={{ width: 32, height: 32, borderRadius: 6, background: 'rgba(59,130,246,0.2)', border: '1px solid rgba(59,130,246,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <ImageIcon size={18} style={{ color: '#60a5fa' }} />
                                </div>
                                <div>
                                  <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600, color: 'var(--text-body)' }}>{model.name}</h3>
                                  <div style={{ fontSize: 11, color: '#64748b' }}>ID: {model.id}</div>
                                </div>
                              </div>

                              {/* 真实模型名 */}
                              <div style={{ marginBottom: 12 }}>
                                <label style={{ fontSize: 11, color: '#64748b', marginBottom: 4, display: 'block' }}>真实模型名（可修改）</label>
                                <input 
                                  style={inputStyle} 
                                  value={modelRealName} 
                                  onChange={(e) => updateMjRealName(model.id, e.target.value)} 
                                  placeholder="API 调用的真实模型名" 
                                />
                              </div>

                              {/* 自定义名称 */}
                              <div style={{ marginBottom: 12 }}>
                                <label style={{ fontSize: 11, color: '#64748b', marginBottom: 4, display: 'block' }}>自定义名称</label>
                                <input 
                                  style={inputStyle} 
                                  value={modelDisplayName} 
                                  onChange={(e) => updateMjDisplayName(model.id, e.target.value)} 
                                  placeholder="你自己取的名字，显示在图板节点上" 
                                />
                              </div>

                              {/* 模型说明 */}
                              <div style={{ marginBottom: 12 }}>
                                <label style={{ fontSize: 11, color: '#64748b', marginBottom: 4, display: 'block' }}>模型说明（可选）</label>
                                <input 
                                  style={inputStyle} 
                                  value={modelDescription} 
                                  onChange={(e) => updateMjDescription(model.id, e.target.value)} 
                                  placeholder="一句话介绍模型特点，如：写实风格、画面细腻..." 
                                />
                              </div>

                              {/* 基础积分 */}
                              <div style={{ marginBottom: 12 }}>
                                <label style={{ fontSize: 11, color: '#64748b', marginBottom: 4, display: 'block' }}>基础积分消耗</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                  <span style={{ display: 'flex', alignItems: 'center' }}><Sparkle size={16} color="#fbbf24" strokeWidth={2.5} /></span>
                                  <input 
                                    type="number" 
                                    min="0" 
                                    step="0.01" 
                                    style={{ ...inputStyle, width: 120, border: '2px solid #ef4444', background: 'rgba(239,68,68,0.08)' }} 
                                    value={modelCreditCost} 
                                    onChange={(e) => updateMjCreditCost(model.id, Math.max(0, parseFloat(e.target.value) || 0))} 
                                    placeholder="0" 
                                  />
                                  <span style={{ fontSize: 12, color: '#64748b' }}>积分/次</span>
                                </div>
                                <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>基础价格，实际消耗 = 基础积分 × 参数倍率 × 批次数。设 0 = 免费</div>
                              </div>

                              {/* 参数配置（工具箱模型跳过，所有功能默认全启用） */}
                              {model.category !== 'toolbox' && model.parameters && Object.keys(model.parameters).length > 0 && (
                                <div>
                                  <label style={{ fontSize: 11, color: '#64748b', marginBottom: 8, display: 'block' }}>启用的参数值（复选框勾选）</label>
                                  <div style={{ padding: 12, background: 'var(--modal-input-bg)', borderRadius: 8 }}>
                                    {Object.entries(model.parameters).map(([key, param]) => (
                                      <div key={key} style={{ marginBottom: 12 }}>
                                        <div style={{ fontSize: 12, color: '#60a5fa', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                                          {param.label || key}
                                          {param.affectsPrice && <span style={{ fontSize: 10, color: '#fbbf24', background: 'rgba(251,191,36,0.15)', padding: '1px 6px', borderRadius: 4 }}>💰 影响价格</span>}
                                        </div>
                                        {param.type === 'select' && param.options && (
                                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                            {param.options.map((opt) => {
                                              const isChecked = (modelEnabledParams[key] || []).includes(opt);
                                              const displayLabel = param.optionLabels?.[opt] || opt;
                                              const multValue = modelMultipliers[key]?.[opt];
                                              return (
                                                <div key={opt} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: isChecked ? 'var(--text-body)' : '#64748b', cursor: 'pointer', padding: '0 8px', height: 26, boxSizing: 'border-box', background: isChecked ? 'rgba(59,130,246,0.2)' : 'var(--modal-input-bg)', borderRadius: 4, border: `1px solid ${isChecked ? '#3b82f6' : 'rgba(255,255,255,0.08)'}`, userSelect: 'none' }}>
                                                    <input 
                                                      type="checkbox" 
                                                      checked={isChecked} 
                                                      onChange={() => updateMjParam(model.id, key, opt)} 
                                                      style={{ accentColor: '#3b82f6' }} 
                                                    />
                                                    {displayLabel}
                                                  </label>
                                                  {param.affectsPrice && isChecked && (
                                                    <input 
                                                      type="number" 
                                                      min="0.1" 
                                                      step="0.1" 
                                                      value={multValue ?? 1} 
                                                      onChange={(e) => { 
                                                        const val = parseFloat(e.target.value) || 1; 
                                                        updateMjMultiplier(model.id, key, opt, val); 
                                                      }} 
                                                      style={{ width: 36, height: 26, boxSizing: 'border-box', padding: '0 2px', fontSize: 11, background: 'rgba(251,191,36,0.1)', border: '1px solid #fbbf24', borderRadius: 4, color: '#fbbf24', textAlign: 'center', outline: 'none' }} 
                                                      title={`倍率：实际积分 = 基础积分 × ${multValue ?? 1}`} 
                                                    />
                                                  )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* 工具箱模型：列出所有动作，可启用/禁用 + 自定义名称 */}
                              {model.category === 'toolbox' && model.parameters?.action_type?.options && (() => {
                                const disabledSet = new Set(mjEnabledParams[model.id]?._disabledActions || []);
                                const toggleAction = (opt) => {
                                  setMjEnabledParams(prev => {
                                    const old = prev[model.id] || {};
                                    const oldDisabled = new Set(old._disabledActions || []);
                                    if (oldDisabled.has(opt)) oldDisabled.delete(opt); else oldDisabled.add(opt);
                                    return { ...prev, [model.id]: { ...old, _disabledActions: [...oldDisabled] } };
                                  });
                                };
                                const renderRow = (opt) => {
                                  const defaultLabel = model.parameters.action_type.optionLabels?.[opt] || opt;
                                  const customLabel = (mjEnabledParams[model.id]?._actionLabels || {})[opt] || '';
                                  const isDisabled = disabledSet.has(opt);
                                  return (
                                    <div key={opt} style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: isDisabled ? 0.45 : 1, transition: 'opacity 0.15s' }}>
                                      <div onClick={() => toggleAction(opt)} style={{ width: 18, height: 18, borderRadius: 4, border: '1.5px solid ' + (isDisabled ? '#475569' : '#22d3ee'), background: isDisabled ? 'transparent' : 'rgba(34,211,238,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, transition: 'all 0.15s' }}>
                                        {!isDisabled && <span style={{ fontSize: 11, color: '#22d3ee', fontWeight: 700, lineHeight: 1 }}>✓</span>}
                                      </div>
                                      <div style={{ width: 68, fontSize: 11, color: 'var(--text-secondary)', flexShrink: 0 }}>{defaultLabel}</div>
                                      <input style={{ ...inputStyle, flex: 1, fontSize: 12 }} value={customLabel}
                                        onChange={(e) => { const val = e.target.value; setMjEnabledParams(prev => { const old = prev[model.id] || {}; const oldLabels = old._actionLabels || {}; return { ...prev, [model.id]: { ...old, _actionLabels: { ...oldLabels, [opt]: val } } }; }); }}
                                        placeholder={defaultLabel} />
                                    </div>
                                  );
                                };
                                return (
                                <div>
                                  <label style={{ fontSize: 11, color: '#64748b', marginBottom: 8, display: 'block' }}>工具箱功能（点击复选框启用/禁用，可自定义名称）</label>
                                  <div style={{ padding: 12, background: 'var(--modal-input-bg)', borderRadius: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                                    <div style={{ fontSize: 10, color: '#ef4444', fontWeight: 600, marginBottom: 2 }}>▸ 四宫格阶段</div>
                                    {(model.parameters.action_type.groups?.grid || []).map(renderRow)}
                                    <div style={{ fontSize: 10, color: '#daa520', fontWeight: 600, marginTop: 8, marginBottom: 2 }}>▸ 放大后阶段</div>
                                    {(model.parameters.action_type.groups?.upscaled || []).map(renderRow)}
                                  </div>
                                  <div style={{ fontSize: 10, color: '#64748b', marginTop: 6 }}>取消勾选可隐藏对应工具栏按钮。留空名称则使用默认名称。</div>
                                </div>
                                );
                              })()}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* ── 普通单能力模型配置（含已保存模型；排除 Midjourney 新建专用模式） ── */}
                    {currentVendor && selectedModel && !(activeSelection.type === 'provider' && activeSelection.vendorId === 'yunwu-midjourney') && (
                      <>
                        {/* ── 自定义名称 ── */}
                        <div>
                          <label style={labelStyle}>{currentModel?.category === 'toolbox' ? '模型名称（显示在选择框里）' : '自定义名称'}</label>
                          <input style={inputStyle} value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder={currentModel?.category === 'toolbox' ? '如：豆包 / Claude（显示在分镜大师选择框里的名字）' : '你自己取的名字，显示在图板节点上'} />
                        </div>

                        {/* ── 模型说明 ── */}
                        {currentModel?.category !== 'toolbox' && <div>
                          <label style={labelStyle}>模型说明（可选）</label>
                          <input style={inputStyle} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="一句话介绍模型特点，如：写实风格、画面细腻..." />
                        </div>}

                        {/* ── 基础积分 ──（语言模型改用「输出单价/千token」计费，隐藏基础积分避免重复） */}
                        {selectedModel !== 'model_master' && !currentModel?.isLanguageModel && (
                        <div>
                          <label style={labelStyle}>基础积分消耗</label>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ display: 'flex', alignItems: 'center' }}><Sparkle size={16} color="#fbbf24" strokeWidth={2.5} /></span>
                            <input type="number" min="0" step="1" style={{ ...inputStyle, width: 120, border: '2px solid #ef4444', background: 'rgba(239,68,68,0.08)' }} value={creditCost} onChange={(e) => setCreditCost(Math.max(0, parseInt(e.target.value) || 0))} placeholder="0" />
                            <span style={{ fontSize: 12, color: '#64748b' }}>积分/次</span>
                          </div>
                          <div style={{ fontSize: 10, color: '#64748b', marginTop: 4 }}>基础价格，实际消耗 = 基础积分 × 参数倍率 × 批次数。设 0 = 免费</div>
                        </div>
                        )}

                        {/* ── 模型类别（仅新建时显示；工具箱模型不显示） ── */}
                        {activeSelection.type === 'provider' && currentModel?.category !== 'toolbox' && (
                          <div>
                            <label style={labelStyle}>模型类别</label>
                            <div style={{ display: 'flex', gap: 8 }}>
                              <button onClick={() => setModelType('image')} style={{ ...btn(modelType === 'image'), flex: 1, padding: '8px 12px' }}>图像模型</button>
                              <button onClick={() => setModelType('video')} style={{ ...btn(modelType === 'video'), flex: 1, padding: '8px 12px', background: modelType === 'video' ? '#8b5cf6' : 'transparent', borderColor: modelType === 'video' ? '#7c3aed' : 'rgba(255,255,255,0.12)' }}>视频模型</button>
                            </div>
                          </div>
                        )}

                        {/* ── 参数配置（新建 + 已保存都显示；工具箱模型跳过） ── */}
                        {currentModel?.category !== 'toolbox' && (() => {
                          const definedParams = currentModel?.parameters || {};
                          const fallbackParams = Object.fromEntries(
                            Object.entries(enabledParams || {})
                              .filter(([key, val]) => key !== '_multipliers' && Array.isArray(val) && val.length > 0)
                              .map(([key, val]) => [key, { type: 'select', label: key, options: val }])
                          );
                          const paramsToRender = Object.keys(definedParams).length > 0 ? definedParams : fallbackParams;
                          if (!paramsToRender || Object.keys(paramsToRender).length === 0) return null;

                          const updateMultiplier = (paramKey, option, value) => {
                            setMultipliers(prev => ({
                              ...prev,
                              [paramKey]: {
                                ...(prev[paramKey] || {}),
                                [option]: value
                              }
                            }));
                          };

                          return (
                            <div>
                              <label style={labelStyle}>启用的参数值（复选框勾选）</label>
                              <div style={{ padding: 12, background: 'var(--modal-input-bg)', borderRadius: 8 }}>
                                {Object.entries(paramsToRender).map(([key, param]) => (
                                  <div key={key} style={{ marginBottom: 12 }}>
                                    <div style={{ fontSize: 12, color: '#60a5fa', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                                      {param.label || key}
                                      {param.affectsPrice && <span style={{ fontSize: 10, color: '#fbbf24', background: 'rgba(251,191,36,0.15)', padding: '1px 6px', borderRadius: 4 }}>💰 影响价格</span>}
                                    </div>

                                    {param.type === 'select' && param.options && (() => {
                                      // 🔀 多口交集：只渲染所有口都支持的选项；被交集砍掉的隐藏并提示
                                      const allowed = channelIntersection?.optionsByParam?.[key];
                                      const renderOpts = allowed ? param.options.filter(o => allowed.includes(o)) : param.options;
                                      const hiddenCount = param.options.length - renderOpts.length;
                                      return (
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                        {renderOpts.map((opt) => {
                                          const isChecked = (enabledParams[key] || []).includes(opt);
                                          const displayLabel = param.optionLabels?.[opt] || opt;
                                          const multValue = multipliers[key]?.[opt];
                                          return (
                                            <div key={opt} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: isChecked ? 'var(--text-body)' : '#64748b', cursor: 'pointer', padding: '0 8px', height: 26, boxSizing: 'border-box', background: isChecked ? 'rgba(59,130,246,0.2)' : 'var(--modal-input-bg)', borderRadius: 4, border: `1px solid ${isChecked ? '#3b82f6' : 'rgba(255,255,255,0.08)'}`, userSelect: 'none' }}>
                                                <input type="checkbox" checked={isChecked} onChange={() => handleParamToggle(key, opt)} style={{ accentColor: '#3b82f6' }} />
                                                {displayLabel}
                                              </label>
                                              {param.affectsPrice && isChecked && (
                                                <input
                                                  type="number"
                                                  min="0.1"
                                                  step="0.1"
                                                  value={multValue ?? 1}
                                                  onChange={(e) => updateMultiplier(key, opt, parseFloat(e.target.value) || 1)}
                                                  style={{ width: 36, height: 26, boxSizing: 'border-box', padding: '0 2px', fontSize: 11, background: 'rgba(251,191,36,0.1)', border: '1px solid #fbbf24', borderRadius: 4, color: '#fbbf24', textAlign: 'center', outline: 'none' }}
                                                />
                                              )}
                                            </div>
                                          );
                                        })}
                                        {hiddenCount > 0 && (
                                          <span style={{ fontSize: 10, color: '#f59e0b', alignSelf: 'center' }}>已按多口交集隐藏 {hiddenCount} 项（部分接口不支持）</span>
                                        )}
                                      </div>
                                      );
                                    })()}

                                    {param.type === 'number' && Array.isArray(param.options) && param.options.length > 0 && (
                                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                        {param.options.map((opt) => {
                                          const isChecked = (enabledParams[key] || []).includes(opt);
                                          const multValue = multipliers[key]?.[opt];
                                          return (
                                            <div key={opt} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                              <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: isChecked ? 'var(--text-body)' : '#64748b', cursor: 'pointer', padding: '0 8px', height: 26, boxSizing: 'border-box', background: isChecked ? 'rgba(59,130,246,0.2)' : 'var(--modal-input-bg)', borderRadius: 4, border: `1px solid ${isChecked ? '#3b82f6' : 'rgba(255,255,255,0.08)'}`, userSelect: 'none' }}>
                                                <input type="checkbox" checked={isChecked} onChange={() => handleParamToggle(key, opt)} style={{ accentColor: '#3b82f6' }} />
                                                {opt}
                                              </label>
                                              {param.affectsPrice && isChecked && (
                                                <input
                                                  type="number"
                                                  min="0.1"
                                                  step="0.1"
                                                  value={multValue ?? 1}
                                                  onChange={(e) => updateMultiplier(key, opt, parseFloat(e.target.value) || 1)}
                                                  style={{ width: 36, height: 26, boxSizing: 'border-box', padding: '0 2px', fontSize: 11, background: 'rgba(251,191,36,0.1)', border: '1px solid #fbbf24', borderRadius: 4, color: '#fbbf24', textAlign: 'center', outline: 'none' }}
                                                />
                                              )}
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}

                                    {param.type === 'boolean' && (
                                      <div style={{ display: 'flex', gap: 8 }}>
                                        {[true, false].map((opt) => {
                                          const isChecked = (enabledParams[key] || []).includes(opt);
                                          return (
                                            <label key={String(opt)} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, color: isChecked ? '#e2e8f0' : '#94a3b8', cursor: 'pointer', padding: '0 8px', height: 26, boxSizing: 'border-box', background: isChecked ? 'rgba(59,130,246,0.2)' : 'var(--modal-input-bg)', borderRadius: 4, border: `1px solid ${isChecked ? '#3b82f6' : 'rgba(255,255,255,0.08)'}`, userSelect: 'none' }}>
                                              <input type="checkbox" checked={isChecked} onChange={() => handleParamToggle(key, opt)} style={{ accentColor: '#3b82f6' }} />
                                              <span style={{ color: isChecked ? '#e2e8f0' : '#94a3b8' }}>{opt ? '开启' : '关闭'}</span>
                                            </label>
                                          );
                                        })}
                                      </div>
                                    )}

                                    {param.type === 'lora' && (
                                      <div>
                                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 8 }}>
                                          配置可用的 LoRA 模型列表（用户在节点中输入 # 可选择）。文件名填写 .safetensors 文件的完整名称。
                                        </div>
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                          {loraList.map((lora, idx) => (
                                            <div key={idx} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: 10, background: 'var(--modal-input-bg)', borderRadius: 6, border: '1px solid rgba(255,255,255,0.08)' }}>
                                              {/* 缩略图上传 */}
                                              <div style={{ flexShrink: 0 }}>
                                                <div style={{ width: 60, height: 60, borderRadius: 6, background: 'rgba(255,255,255,0.05)', border: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', position: 'relative' }}>
                                                  {lora.thumbnail ? (
                                                    <img src={lora.thumbnail} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="" onError={(e) => e.target.style.display = 'none'} />
                                                  ) : (
                                                    <Sparkles size={20} style={{ color: '#64748b', opacity: 0.5 }} />
                                                  )}
                                                </div>
                                                <input
                                                  type="file"
                                                  accept="image/png,image/jpeg,image/webp"
                                                  style={{ display: 'none' }}
                                                  id={`lora-thumb-${idx}`}
                                                  onChange={async (e) => {
                                                    const file = e.target.files?.[0];
                                                    if (!file) return;
                                                    try {
                                                      const buffer = await file.arrayBuffer();
                                                      const hashHex = await sha256Hex(buffer);
                                                      if (hashHex) {
                                                        const checkResult = await api.get(`/api/upload/check-hash?hash=${hashHex}&type=logo`);
                                                        if (checkResult.exists && checkResult.url) {
                                                          const newList = [...loraList];
                                                          newList[idx] = { ...newList[idx], thumbnail: checkResult.url };
                                                          setLoraList(newList);
                                                          e.target.value = '';
                                                          return;
                                                        }
                                                      }
                                                      const formData = new FormData();
                                                      formData.append('file', file);
                                                      const { getToken: getAuthToken } = await import('../api');
                                                      const result = await fetch(`${C.API_BASE_URL}/api/upload?type=logo`, {
                                                        method: 'POST',
                                                        headers: { 'Authorization': `Bearer ${getAuthToken()}` },
                                                        body: formData
                                                      }).then(r => r.json());
                                                      if (result.url) {
                                                        const newList = [...loraList];
                                                        newList[idx] = { ...newList[idx], thumbnail: result.url };
                                                        setLoraList(newList);
                                                      } else if (result.error) {
                                                        showToast('❌ 上传失败: ' + result.error, 'warning');
                                                      }
                                                    } catch (err) {
                                                      console.error('上传缩略图失败:', err);
                                                      showToast('❌ 上传失败', 'warning');
                                                    }
                                                    e.target.value = '';
                                                  }}
                                                />
                                                <label
                                                  htmlFor={`lora-thumb-${idx}`}
                                                  style={{ display: 'block', marginTop: 4, padding: '4px 8px', background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 4, color: '#60a5fa', fontSize: 10, cursor: 'pointer', textAlign: 'center', userSelect: 'none' }}
                                                >
                                                  {lora.thumbnail ? '换图' : '上传'}
                                                </label>
                                                {lora.thumbnail && (
                                                  <button
                                                    onClick={() => {
                                                      const newList = [...loraList];
                                                      newList[idx] = { ...newList[idx], thumbnail: null };
                                                      setLoraList(newList);
                                                    }}
                                                    style={{ display: 'block', width: '100%', marginTop: 2, padding: '2px 4px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 4, color: '#f87171', fontSize: 10, cursor: 'pointer' }}
                                                  >
                                                    清除
                                                  </button>
                                                )}
                                              </div>

                                              {/* 表单字段 */}
                                              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                <div>
                                                  <label style={{ fontSize: 10, color: '#64748b', marginBottom: 2, display: 'block' }}>显示名称（用户看到的）</label>
                                                  <input
                                                    style={{ ...inputStyle, fontSize: 12, padding: '6px 10px' }}
                                                    value={lora.label || ''}
                                                    onChange={(e) => {
                                                      const newList = [...loraList];
                                                      newList[idx] = { ...newList[idx], label: e.target.value };
                                                      setLoraList(newList);
                                                    }}
                                                    placeholder="极线暗黑美学风"
                                                  />
                                                </div>
                                                <div>
                                                  <label style={{ fontSize: 10, color: '#64748b', marginBottom: 2, display: 'block' }}>文件名（传给 API 的 .safetensors 文件名）</label>
                                                  <input
                                                    style={{ ...inputStyle, fontSize: 11, padding: '6px 10px', fontFamily: 'monospace' }}
                                                    value={lora.name || ''}
                                                    onChange={(e) => {
                                                      const newList = [...loraList];
                                                      newList[idx] = { ...newList[idx], name: e.target.value };
                                                      setLoraList(newList);
                                                    }}
                                                    placeholder="ZIT-flow-dpo-lora.safetensors"
                                                  />
                                                </div>
                                              </div>

                                              {/* 删除按钮 */}
                                              <button
                                                onClick={() => {
                                                  setLoraList(loraList.filter((_, i) => i !== idx));
                                                }}
                                                style={{ padding: '8px 10px', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, color: '#f87171', cursor: 'pointer', flexShrink: 0 }}
                                              >
                                                <Trash2 size={16} />
                                              </button>
                                            </div>
                                          ))}
                                          <button
                                            onClick={() => {
                                              setLoraList([...loraList, { name: '', label: '', thumbnail: null }]);
                                            }}
                                            style={{ padding: '10px 14px', background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 6, color: '#60a5fa', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' }}
                                          >
                                            <Plus size={16} />
                                            添加 LoRA
                                          </button>
                                        </div>
                                      </div>
                                    )}

                                    {/* 🆕 autoTrigger 的 number 参数：显示输入框让管理员配置默认值 */}
                                    {param.type === 'number' && !Array.isArray(param.options) && (param.autoTrigger || param.priceAdditionPerSecond) && (
                                      <div>
                                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 6 }}>
                                          该参数运行时自动触发，此处配置默认值
                                        </div>
                                        <input
                                          type="number"
                                          min={param.min ?? 0}
                                          max={param.max}
                                          step={param.step ?? 0.1}
                                          value={enabledParams[key]?.default ?? param.default ?? 0}
                                          onChange={(e) => {
                                            const val = parseFloat(e.target.value) || 0;
                                            setEnabledParams(prev => ({
                                              ...prev,
                                              [key]: { ...prev[key], default: val }
                                            }));
                                          }}
                                          style={{ ...inputStyle, width: 120 }}
                                        />
                                      </div>
                                    )}

                                    {/* 普通 number/text 参数：只显示提示 */}
                                    {((param.type === 'number' && !Array.isArray(param.options) && !param.autoTrigger && !param.priceAdditionPerSecond) || param.type === 'text') && (
                                      <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>该参数在节点中填写具体值，此处仅展示支持项。</div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })()}

                        {/* 工具箱模型：列出所有动作，可启用/禁用 + 自定义名称 */}
                        {currentModel?.category === 'toolbox' && currentModel.parameters?.action_type?.options && (() => {
                          const disabledSet = new Set(enabledParams._disabledActions || []);
                          const toggleAction = (opt) => {
                            setEnabledParams(prev => {
                              const oldDisabled = new Set(prev._disabledActions || []);
                              if (oldDisabled.has(opt)) oldDisabled.delete(opt); else oldDisabled.add(opt);
                              return { ...prev, _disabledActions: [...oldDisabled] };
                            });
                          };
                          const renderRow = (opt) => {
                            const defaultLabel = currentModel.parameters.action_type.optionLabels?.[opt] || opt;
                            const customLabel = (enabledParams._actionLabels || {})[opt] || '';
                            const isDisabled = disabledSet.has(opt);
                            return (
                              <div key={opt} style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: isDisabled ? 0.45 : 1, transition: 'opacity 0.15s' }}>
                                <div onClick={() => toggleAction(opt)} style={{ width: 18, height: 18, borderRadius: 4, border: '1.5px solid ' + (isDisabled ? '#475569' : '#22d3ee'), background: isDisabled ? 'transparent' : 'rgba(34,211,238,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, transition: 'all 0.15s' }}>
                                  {!isDisabled && <span style={{ fontSize: 11, color: '#22d3ee', fontWeight: 700, lineHeight: 1 }}>✓</span>}
                                </div>
                                <div style={{ width: 68, fontSize: 11, color: 'var(--text-secondary)', flexShrink: 0 }}>{defaultLabel}</div>
                                <input style={{ ...inputStyle, flex: 1, fontSize: 12 }} value={customLabel}
                                  onChange={(e) => { const val = e.target.value; setEnabledParams(prev => { const oldLabels = prev._actionLabels || {}; return { ...prev, _actionLabels: { ...oldLabels, [opt]: val } }; }); }}
                                  placeholder={defaultLabel} />
                              </div>
                            );
                          };
                          return (
                          <div>
                            <label style={labelStyle}>工具箱功能（点击复选框启用/禁用，可自定义名称）</label>
                            <div style={{ padding: 12, background: 'var(--modal-input-bg)', borderRadius: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                              <div style={{ fontSize: 10, color: '#ef4444', fontWeight: 600, marginBottom: 2 }}>▸ 四宫格阶段</div>
                              {(currentModel.parameters.action_type.groups?.grid || []).map(renderRow)}
                              <div style={{ fontSize: 10, color: '#daa520', fontWeight: 600, marginTop: 8, marginBottom: 2 }}>▸ 放大后阶段</div>
                              {(currentModel.parameters.action_type.groups?.upscaled || []).map(renderRow)}
                            </div>
                            <div style={{ fontSize: 10, color: '#64748b', marginTop: 6 }}>取消勾选可隐藏对应工具栏按钮。留空名称则使用默认名称。</div>
                          </div>
                          );
                        })()}

                        {/* 工具箱模型：textarea/text 类型参数（如文本润色的系统提示词） */}
                        {currentModel?.category === 'toolbox' && currentModel.parameters && (() => {
                          // runtime: true 的参数是每次请求现场组装的值（如 MJ 的 task_id / index），
                          // 后端只读运行时 userOptions，存进配置无意义 → 不在此展示
                          const textParams = Object.entries(currentModel.parameters).filter(([, p]) => !p.runtime && (p.type === 'textarea' || p.type === 'text' || p.type === 'select'));
                          if (textParams.length === 0) return null;
                          return (
                            <div>
                              <label style={labelStyle}>工具配置</label>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 12, background: 'var(--modal-input-bg)', borderRadius: 8 }}>
                                {textParams.map(([key, param]) => (
                                  <div key={key}>
                                    <div style={{ fontSize: 12, color: '#60a5fa', marginBottom: 6 }}>{param.label || key}</div>
                                    {param.type === 'textarea' ? (
                                      <textarea
                                        style={{ ...inputStyle, minHeight: 120, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.5 }}
                                        value={enabledParams[key] || ''}
                                        onChange={(e) => setEnabledParams(prev => ({ ...prev, [key]: e.target.value }))}
                                        placeholder={param.placeholder || ''}
                                      />
                                    ) : param.type === 'select' ? (
                                      <select
                                        style={{ ...inputStyle, cursor: 'pointer' }}
                                        value={enabledParams[key] || param.default || ''}
                                        onChange={(e) => setEnabledParams(prev => ({ ...prev, [key]: e.target.value }))}
                                      >
                                        {(param.options || []).map(opt => (
                                          <option key={opt} value={opt}>{param.optionLabels?.[opt] || opt}</option>
                                        ))}
                                      </select>
                                    ) : (
                                      <input
                                        style={inputStyle}
                                        value={enabledParams[key] || param.default || ''}
                                        onChange={(e) => setEnabledParams(prev => ({ ...prev, [key]: e.target.value }))}
                                        placeholder={param.placeholder || ''}
                                      />
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })()}

                        {/* 注：分镜大师的草图提示词 / Skills / 画面风格预设 / 触发按钮图标
                            已移到独立的「分镜大师设定」弹窗（左下角入口），与模型解耦，全局共用。
                            此处只保留引擎参数（模型名 / 温度 / 最大长度 / 输出单价）。 */}
                      </>
                    )}

                    {/* ── 操作按钮 ── */}
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <button onClick={handleSave} disabled={isSaveDisabled} style={{ ...btn(true), flex: 1, opacity: isSaveDisabled ? 0.5 : 1, cursor: isSaveDisabled ? 'not-allowed' : 'pointer' }}>
                        {saving ? '保存中...' : (activeSelection.type === 'saved' ? '💾 更新配置' : '💾 保存配置')}
                      </button>
                      {activeSelection.type === 'saved' && (
                        <button onClick={() => { const model = savedModels.find(m => m.id === activeSelection.id); if (model) handleDelete(model.id, model.name); }}
                          style={{ ...btn(false), color: '#f87171', borderColor: '#ef4444' }}>
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {toastMessage && <AlertToast message={toastMessage} type={toastType} position="center" />}
      {confirmModal && <ConfirmModal title={confirmModal.title} message={confirmModal.message} confirmText="确认" cancelText="取消" danger={confirmModal.danger} onConfirm={confirmModal.onConfirm} onCancel={confirmModal.onCancel} />}
      <PresetModal open={presetModalOpen} onClose={() => setPresetModalOpen(false)} />
      <SkillboxConfigModal open={skillboxModalOpen} onClose={() => setSkillboxModalOpen(false)} />
      <StoryboardMasterModal open={storyboardModalOpen} onClose={() => setStoryboardModalOpen(false)} />
      <FeatureSettingsModal open={modelMasterModalOpen} onClose={() => setModelMasterModalOpen(false)}
        feature="model-master" title="建模大师设定" accent="#a78bfa"
        skillsLabel="建模大师 Skills（Agent 设定，所有模型共用）"
        skillsPlaceholder="输入系统提示词，描述建模大师如何根据用户输入生成 Blender 脚本并渲染 3D 场景…"
        intro="这里配的是建模大师「能力」本身，所有模型共用。在下面勾选建模大师可用哪些语言模型（在「已添加模型」里添加的语言模型都可选）。" />
      <FeatureSettingsModal open={textPolishModalOpen} onClose={() => setTextPolishModalOpen(false)}
        feature="text-polish" title="文本润色设定" accent="#34d399" singleSelect
        skillsLabel="文本润色 Skills（Agent 设定）"
        skillsPlaceholder="输入系统提示词，告诉模型如何润色文本…"
        intro="这里配的是文本润色「能力」本身。文本润色按钮是单击直接润色，所以只能选一个语言模型 —— 选哪个，节点编辑器点润色就用哪个。" />
    </div>
  );

  return createPortal(modalContent, document.body);
}
